<template>
  <div class="map-shell">
    <div ref="mapEl" class="map"></div>

    <!-- Панель координаты «Улитка» — показывает B12-3 при наведении курсора -->
    <div class="ulitka-panel" @click.stop>
      <label class="ulitka-panel__toggle">
        <input type="checkbox" v-model="showUlitka" />
        <span>Улитка</span>
      </label>
      <div v-if="showUlitka" class="ulitka-panel__coord">
        {{ currentUlitka || '—' }}
      </div>
      <button
        v-if="!readOnly"
        type="button"
        class="ulitka-panel__gear"
        title="Калибровка сетки (совмещение с картой 1942 г.)"
        @click="showGridCalibration = !showGridCalibration"
      >⚙</button>
    </div>

    <!-- Панель калибровки сетки — по ТЗ п.11 совмещение с бумажной картой 1942 г. -->
    <div
      v-if="showGridCalibration && !readOnly"
      class="grid-calibration-panel"
      @click.stop
    >
      <div class="grid-calibration-panel__title">
        Калибровка сетки
        <button type="button" class="grid-calibration-panel__close" @click="showGridCalibration = false">×</button>
      </div>
      <div class="grid-calibration-panel__hint">
        Совместите линии координатной сетки приложения с линиями бумажной карты 1942 г.
      </div>
      <div class="grid-calibration-panel__row">
        <label>Сдвиг X</label>
        <input type="range" min="-0.1" max="0.1" step="0.001" :value="gridCal.offsetX"
               @input="updateGridCal({ offsetX: Number($event.target.value) })" />
        <span class="grid-calibration-panel__value">{{ (gridCal.offsetX * 100).toFixed(1) }} %</span>
      </div>
      <div class="grid-calibration-panel__row">
        <label>Сдвиг Y</label>
        <input type="range" min="-0.1" max="0.1" step="0.001" :value="gridCal.offsetY"
               @input="updateGridCal({ offsetY: Number($event.target.value) })" />
        <span class="grid-calibration-panel__value">{{ (gridCal.offsetY * 100).toFixed(1) }} %</span>
      </div>
      <div class="grid-calibration-panel__row">
        <label>Клетка X</label>
        <input type="range" min="0.85" max="1.15" step="0.001" :value="gridCal.scaleX"
               @input="updateGridCal({ scaleX: Number($event.target.value) })" />
        <span class="grid-calibration-panel__value">{{ (gridCal.scaleX * 100).toFixed(1) }} %</span>
      </div>
      <div class="grid-calibration-panel__row">
        <label>Клетка Y</label>
        <input type="range" min="0.85" max="1.15" step="0.001" :value="gridCal.scaleY"
               @input="updateGridCal({ scaleY: Number($event.target.value) })" />
        <span class="grid-calibration-panel__value">{{ (gridCal.scaleY * 100).toFixed(1) }} %</span>
      </div>
      <button type="button" class="grid-calibration-panel__reset" @click="resetGridCal">
        Сбросить
      </button>
    </div>

    <!-- Undo/Redo кнопки над картой убраны — теперь только в сайдбаре инструментов.
         Ctrl+Z / Ctrl+Y продолжают работать глобально через window keydown. -->


    <!-- Улитка: вертикальный слайдер масштаба справа
         (для карты 1:200 000 — быстрое приближение/отдаление). -->
    <div v-if="showZoomSlider" class="map-zoom-slider" @click.stop>
      <button
        type="button"
        class="map-zoom-slider__btn"
        title="Приблизить"
        @click="stepZoom(+1)"
      >+</button>

      <div class="map-zoom-slider__track">
        <input
          type="range"
          class="map-zoom-slider__range"
          :min="zoomMin"
          :max="zoomMax"
          step="0.5"
          v-model.number="zoomValue"
          @input="onSliderInput"
          orient="vertical"
          aria-label="Масштаб карты"
        />
        <div class="map-zoom-slider__badge">{{ Math.round(zoomValue * 10) / 10 }}</div>
      </div>

      <button
        type="button"
        class="map-zoom-slider__btn"
        title="Отдалить"
        @click="stepZoom(-1)"
      >−</button>
    </div>

    <!-- Панель выделенного знака: размер, поворот, цвет (по встрече 03.09.2026 —
         «подменю внизу» при клике на знак) -->
    <div v-if="selectedEl && !readOnly" class="el-panel" @click.stop>
      <div class="el-panel__title" :title="selectedEl.label">{{ selectedEl.label }}</div>

      <div class="el-panel__group">
        <span class="el-panel__label">Размер</span>
        <button type="button" class="el-panel__btn" title="Меньше (−)" @click="resizeElUniform(1 / 1.15)">−</button>
        <button type="button" class="el-panel__btn" title="Больше (+)" @click="resizeElUniform(1.15)">+</button>
      </div>

      <div v-for="axis in EL_AXES" :key="axis.key" class="el-panel__group">
        <span class="el-panel__label">{{ axis.label }}</span>
        <button type="button" class="el-panel__btn" :title="`${axis.label}: меньше`" @click="applyElChange({ [axis.key]: selectedEl[axis.key] / 1.15 })">−</button>
        <input
          type="range" class="el-panel__range"
          :min="EL_SCALE_MIN" :max="EL_SCALE_MAX" step="0.05"
          :value="selectedEl[axis.key]"
          @input="applyElChange({ [axis.key]: Number($event.target.value) })"
        />
        <button type="button" class="el-panel__btn" :title="`${axis.label}: больше`" @click="applyElChange({ [axis.key]: selectedEl[axis.key] * 1.15 })">+</button>
        <span class="el-panel__value">{{ Math.round(selectedEl[axis.key] * 100) }}%</span>
      </div>
      <button
        v-if="Math.abs(selectedEl.sx - selectedEl.sy) > 0.001"
        type="button" class="el-panel__btn el-panel__btn--wide"
        title="Вернуть исходные пропорции"
        @click="applyElChange({ sy: selectedEl.sx })"
      >Пропорции 1:1</button>

      <div class="el-panel__group">
        <span class="el-panel__label">Поворот</span>
        <button type="button" class="el-panel__btn" title="−15° (Shift+R)" @click="applyElChange({ rot: selectedEl.rot - 15 })">⟲</button>
        <input
          type="range" class="el-panel__range"
          min="0" max="359" step="1"
          :value="selectedEl.rot"
          @input="applyElChange({ rot: Number($event.target.value) })"
        />
        <button type="button" class="el-panel__btn" title="+15° (R)" @click="applyElChange({ rot: selectedEl.rot + 15 })">⟳</button>
        <button type="button" class="el-panel__btn el-panel__btn--wide" title="Развернуть на 180°" @click="applyElChange({ rot: selectedEl.rot + 180 })">180°</button>
        <span class="el-panel__value">{{ selectedEl.rot }}°</span>
      </div>

      <div v-if="selectedEl.isLib" class="el-panel__group">
        <span class="el-panel__label">Цвет</span>
        <button
          v-for="c in MAP_COLORS"
          :key="c.value"
          type="button"
          class="el-panel__swatch"
          :class="{ 'is-active': c.value.toLowerCase() === (selectedEl.color || '').toLowerCase() }"
          :style="{ backgroundColor: c.value }"
          :title="c.label"
          @click="applyElChange({ color: c.value })"
        ></button>
      </div>

      <button type="button" class="el-panel__delete" title="Удалить (Delete)" @click="emit('request-delete-marker', selectedEl.id)">Удалить</button>
      <button type="button" class="el-panel__close" title="Снять выделение (Esc)" @click="setSelectedMarker(null)">×</button>
    </div>

    <div v-if="initError" class="map-init-error">
      Карта не инициализировалась. Откройте консоль для деталей.
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, nextTick, toRaw } from 'vue'
import L from 'leaflet'

import { createMapMarker, listAllMapMarkers, deleteMapMarker, updateMapMarker } from '@/api/chpoMap'
import { addGrid } from '@/components/maps/map.js'
import { preloadImage } from '@/assets/mapPreload'
import { getLineElement, lineElementSvg } from '@/components/maps/lineElements'
import { MAP_COLORS } from '@/components/maps/mapPalette'

const emit = defineEmits(['request-delete-marker', 'marker-deleted'])

const props = defineProps({
  selectedIcon: { type: String, default: null },
  selectedColor: { type: String, default: '#000000' },
  fillColor: { type: String, default: '#DC2626' },
  fillPattern: { type: String, default: 'solid' },
  areaDash: { type: String, default: 'solid' },
  // Постановка знаков: прилипать к ближайшей линии/границе и где ставить
  // относительно неё — 'on' (на линии), 'left' / 'right' (сбоку).
  snapToLine: { type: Boolean, default: true },
  elementSide: { type: String, default: 'on' },
  strokeWidth: { type: Number, default: 3 },
  readOnly: { type: Boolean, default: false },
})

// Пресеты стилей линий (соответствуют шаблонам «Гибкие знаки» в IconsSidebar).
// Ключ — как приходит в selectedIcon, значение — Leaflet-стиль полилинии + мета:
//   type    → сохраняется в img_name как  "line:COLOR:TYPE:PTS", по нему определяется вид при перезагрузке
//   double  → рисовать пару параллельных линий
const LINE_PRESETS = {
  draw_freehand:      { type: 'freehand', dashArray: null,          double: false, endArrow: false },
  draw_solid:         { type: 'solid',    dashArray: null,          double: false, endArrow: false },
  draw_dashed:        { type: 'dashed',   dashArray: '10 6',        double: false, endArrow: false },
  draw_dotted:        { type: 'dotted',   dashArray: '2 6',         double: false, endArrow: false, linecap: 'round' },
  draw_dashdot:       { type: 'dashdot',  dashArray: '10 4 2 4',    double: false, endArrow: false },
  draw_thick:         { type: 'thick',    dashArray: null,          double: false, endArrow: false, weightAdd: 3 },
  draw_double:        { type: 'double',   dashArray: null,          double: true,  endArrow: false },
  draw_double_dashed: { type: 'double_dashed', dashArray: '8 4',    double: true,  endArrow: false },
  draw_arrow:         { type: 'arrow',    dashArray: null,          double: false, endArrow: true },
  draw_arrow_double:  { type: 'arrow_double', dashArray: null,      double: true,  endArrow: true },
  // По ТЗ 03.09.2026: КСП — двойная сплошная (края полосы) + диагональная
  // штриховка внутри. Реализуется через SVG-pattern stroke на широком
  // полилайне (см. ensureKspHatchDefs). Цвет — из selectedColor.
  draw_ksp:           { type: 'ksp',      dashArray: null,          double: false, endArrow: false, weightAdd: 8, linecap: 'butt' },
  // Обычные широкие полосы (сплошная / пунктирная / точечная) — по ТЗ 03.09:
  // «добавь обычные полосу и тд, все что может понадобиться, и чтобы их цвет менялся».
  draw_band_solid:    { type: 'band',        dashArray: null,       double: false, endArrow: false, weightAdd: 8, linecap: 'butt' },
  draw_band_dashed:   { type: 'band_dashed', dashArray: '18 10',    double: false, endArrow: false, weightAdd: 8, linecap: 'butt' },
  draw_band_dotted:   { type: 'band_dotted', dashArray: '2 10',     double: false, endArrow: false, weightAdd: 8, linecap: 'round' },
  draw_fence:         { type: 'fence',    dashArray: null,          double: false, endArrow: false, fence: true },
}

function stylePresetFor(tool) {
  return LINE_PRESETS[tool] || null
}

// === Штриховая заливка полигонов (по ТЗ п.9: hatched-паттерн вместо просто прозрачной) ===
// Идея: инжектируем <defs><pattern>...</pattern></defs> в SVG-канву Leaflet
// (overlayPane). Для каждого цвета создаём уникальный pattern с id вида
// "saqshy-hatch-<hex>". Полигоны с className='hatched-fill' получают
// fill="url(#saqshy-hatch-<hex>)" через .setAttribute() после добавления.
const _hatchCache = new Set()

function hatchIdFor(color) {
  const clean = String(color || '#DC2626').replace('#', '')
  return `saqshy-hatch-${clean.toLowerCase()}`
}

function ensureHatchDefs(color) {
  const id = hatchIdFor(color)
  if (_hatchCache.has(id)) return id
  const map = mapRef.value
  if (!map) return id
  // Находим SVG-канву overlay-панели
  const overlaySvg = map.getPanes().overlayPane.querySelector('svg')
  if (!overlaySvg) return id
  let defs = overlaySvg.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    overlaySvg.insertBefore(defs, overlaySvg.firstChild)
  }
  const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern')
  pattern.setAttribute('id', id)
  pattern.setAttribute('width', '10')
  pattern.setAttribute('height', '10')
  pattern.setAttribute('patternUnits', 'userSpaceOnUse')
  pattern.setAttribute('patternTransform', 'rotate(45)')
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
  line.setAttribute('x1', '0')
  line.setAttribute('y1', '0')
  line.setAttribute('x2', '0')
  line.setAttribute('y2', '10')
  line.setAttribute('stroke', color)
  line.setAttribute('stroke-width', '3')
  pattern.appendChild(line)
  defs.appendChild(pattern)
  _hatchCache.add(id)
  return id
}

// По ТЗ 03.09.2026: КСП рисуется как двойная сплошная линия по краям полосы
// + диагональная штриховка внутри — стандартный топографический знак.
// Реализация: SVG-pattern применяется как stroke к широкой полилинии (weight=KSP_WEIGHT).
// Ширина pattern = KSP_WEIGHT: верхняя и нижняя линии станут краями полосы,
// диагонали заполнят внутренность. Цвет привязан к выбранному пользователем.
const KSP_WEIGHT = 14
function kspHatchIdFor(color) {
  const clean = String(color || '#111111').replace('#', '')
  return `saqshy-ksp-hatch-${clean.toLowerCase()}`
}
function ensureKspHatchDefs(color) {
  const id = kspHatchIdFor(color)
  if (_hatchCache.has(id)) return id
  const map = mapRef.value
  if (!map) return id
  const overlaySvg = map.getPanes().overlayPane.querySelector('svg')
  if (!overlaySvg) return id
  let defs = overlaySvg.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    overlaySvg.insertBefore(defs, overlaySvg.firstChild)
  }
  const svgNs = 'http://www.w3.org/2000/svg'
  const pattern = document.createElementNS(svgNs, 'pattern')
  pattern.setAttribute('id', id)
  // Tile: 12 (ширина шага штриха) × KSP_WEIGHT (высота = высота полосы).
  pattern.setAttribute('width', '12')
  pattern.setAttribute('height', String(KSP_WEIGHT))
  pattern.setAttribute('patternUnits', 'userSpaceOnUse')
  // Верхний край полосы
  const top = document.createElementNS(svgNs, 'line')
  top.setAttribute('x1', '0'); top.setAttribute('y1', '1')
  top.setAttribute('x2', '12'); top.setAttribute('y2', '1')
  top.setAttribute('stroke', color); top.setAttribute('stroke-width', '2')
  pattern.appendChild(top)
  // Нижний край полосы
  const bot = document.createElementNS(svgNs, 'line')
  bot.setAttribute('x1', '0'); bot.setAttribute('y1', String(KSP_WEIGHT - 1))
  bot.setAttribute('x2', '12'); bot.setAttribute('y2', String(KSP_WEIGHT - 1))
  bot.setAttribute('stroke', color); bot.setAttribute('stroke-width', '2')
  pattern.appendChild(bot)
  // Диагональная штриховка внутри полосы (от левого-верха к правому-низу)
  const diag = document.createElementNS(svgNs, 'line')
  diag.setAttribute('x1', '0'); diag.setAttribute('y1', '0')
  diag.setAttribute('x2', '12'); diag.setAttribute('y2', String(KSP_WEIGHT))
  diag.setAttribute('stroke', color); diag.setAttribute('stroke-width', '1.5')
  pattern.appendChild(diag)
  defs.appendChild(pattern)
  _hatchCache.add(id)
  return id
}
function applyKspHatchStroke(polyline, color) {
  const id = ensureKspHatchDefs(color)
  // _path — SVGPathElement Leaflet-полилинии
  const path = polyline && polyline._path
  if (path) {
    path.setAttribute('stroke', `url(#${id})`)
    path.setAttribute('stroke-linecap', 'butt')
    // fill не рисуем — только stroke
    path.setAttribute('fill', 'none')
  }
}

function applyHatchedFill(polygon, strokeColor) {
  const id = ensureHatchDefs(strokeColor || '#DC2626')
  // path создаётся после addTo — берём его и переопределяем fill
  setTimeout(() => {
    const el = polygon.getElement && polygon.getElement()
    if (el) el.setAttribute('fill', `url(#${id})`)
  }, 0)
}

// Наконечник стрелки на конце полилинии. Реализован как маленький
// L.marker с divIcon, повёрнутым под угол последнего сегмента.
// Прикрепляется к базовой полилинии (или layerGroup) на карте.
function attachArrowMarker(baseLayer, pts, color) {
  if (!pts || pts.length < 2 || !mapRef.value) return
  const p1 = pts[pts.length - 2]
  const p2 = pts[pts.length - 1]
  const map = mapRef.value
  const pt1 = map.latLngToLayerPoint(L.latLng(p1[0] ?? p1.lat, p1[1] ?? p1.lng))
  const pt2 = map.latLngToLayerPoint(L.latLng(p2[0] ?? p2.lat, p2[1] ?? p2.lng))
  const angle = Math.atan2(pt2.y - pt1.y, pt2.x - pt1.x) * 180 / Math.PI
  const size = 22
  const half = size / 2
  const html = `<div style="width:${size}px;height:${size}px;transform:rotate(${angle}deg);transform-origin:center center;">
      <svg viewBox="0 0 24 24" width="${size}" height="${size}">
        <polygon points="4,4 22,12 4,20 8,12" fill="${color}" stroke="${color}" stroke-width="1"/>
      </svg>
    </div>`
  const icon = L.divIcon({ className: 'arrow-cap', html, iconSize: [size, size], iconAnchor: [half, half] })
  const tip = L.latLng(p2[0] ?? p2.lat, p2[1] ?? p2.lng)
  const arrow = L.marker(tip, { icon, interactive: false, keyboard: false }).addTo(map)
  // Сохраним ссылку, чтобы удалить вместе с базовой линией
  if (baseLayer instanceof L.LayerGroup) baseLayer.addLayer(arrow)
  else baseLayer._arrowCap = arrow
}
function lineStyleFor(tool) {
  const p = stylePresetFor(tool) || { type: 'solid' }
  const color = p.colorOverride || props.selectedColor || '#DC2626'
  const weight = (props.strokeWidth || 3) + (p.weightAdd || 0)
  const style = { color, weight }
  if (p.dashArray) style.dashArray = p.dashArray
  if (p.linecap) style.lineCap = p.linecap
  if (p.type === 'ksp') style.className = 'ksp-line'
  return { style, preset: p, type: p.type }
}

// По ТЗ 03.09.2026 (баг: «двойные линии не работают»): для двойных линий
// нужны две настоящие параллельные линии (шоссе, рубеж), а не «широкий stroke
// + белая внутренняя полоска». Реализация: смещение по нормали к каждому
// сегменту в пиксельных координатах — тогда offset одинаковый на любом зуме
// (при перерисовке при zoomend будем пересчитывать через updateDoubleOffset).
const DOUBLE_OFFSET_PX = 5
function _perpOffsetLatLng(map, prevPt, nextPt, latlng, offsetPx) {
  const c = map.latLngToContainerPoint(latlng)
  // Вектор направления по среднему касательному
  const p = prevPt ? map.latLngToContainerPoint(prevPt) : c
  const n = nextPt ? map.latLngToContainerPoint(nextPt) : c
  let dx = n.x - p.x, dy = n.y - p.y
  const len = Math.hypot(dx, dy) || 1
  dx /= len; dy /= len
  // Перпендикуляр (поворот на 90°)
  const nx = -dy, ny = dx
  return { a: map.containerPointToLatLng(L.point(c.x + nx * offsetPx, c.y + ny * offsetPx)),
           b: map.containerPointToLatLng(L.point(c.x - nx * offsetPx, c.y - ny * offsetPx)) }
}
function parallelOffsetPoints(map, pts, offsetPx) {
  if (!map || !Array.isArray(pts) || pts.length < 2) return { left: [], right: [] }
  const left = [], right = []
  for (let i = 0; i < pts.length; i++) {
    const prev = i > 0 ? pts[i - 1] : null
    const next = i < pts.length - 1 ? pts[i + 1] : null
    const { a, b } = _perpOffsetLatLng(map, prev, next, pts[i], offsetPx)
    left.push(a); right.push(b)
  }
  return { left, right }
}

// По ТЗ 03.09.2026 (баг: КСП рисуется как одна лента вместо «двух полос со штрихом»):
// строим слой из трёх компонентов —
//   1) верхняя линия (левый край полосы)
//   2) нижняя линия (правый край)
//   3) короткие поперечные штрихи между ними — по всей длине через каждые ~SPACING px.
// Не используем SVG-pattern (перезатирается Leaflet при обновлении стиля),
// не используем толстый stroke url(#pattern) (терялся на прод-сборке).
const KSP_HALF_WIDTH_PX = 10   // расстояние от осевой до края полосы (ширина полосы = 20 px)
const KSP_TICK_SPACING_PX = 12 // шаг штриха вдоль полосы
const KSP_STROKE_WEIGHT = 2    // толщина ВСЕХ линий (края и штрихи) — фиксированная,
                               // не зависит от выбора «толщина» в панели, чтобы полоса
                               // выглядела как топознак «две тонкие линии + штрихи»,
                               // а не «сплошная жирная лента».
function buildKspLayer(map, pts, color/*, weight ignored */) {
  const opts = { color, weight: KSP_STROKE_WEIGHT, interactive: false, lineCap: 'butt', lineJoin: 'miter' }
  const { left, right } = parallelOffsetPoints(map, pts, KSP_HALF_WIDTH_PX)
  const outerA = L.polyline(left, opts)
  const outerB = L.polyline(right, opts)
  // Диагональные штрихи между двумя линиями — идём по осевой, каждые SPACING px
  // ставим короткий отрезок под углом ~45° (для наглядности штрих смещён по оси
  // на HALF, чтобы визуально был диагональным, а не строго перпендикулярным).
  const ticks = []
  let carry = 0
  for (let i = 1; i < pts.length; i++) {
    const p1 = map.latLngToContainerPoint(pts[i - 1])
    const p2 = map.latLngToContainerPoint(pts[i])
    let dx = p2.x - p1.x, dy = p2.y - p1.y
    const segLen = Math.hypot(dx, dy)
    if (!segLen) continue
    const ux = dx / segLen, uy = dy / segLen
    // Нормаль (перпендикуляр)
    const nx = -uy, ny = ux
    let dist = KSP_TICK_SPACING_PX - carry
    while (dist <= segLen) {
      const cx = p1.x + ux * dist
      const cy = p1.y + uy * dist
      // Смещение вдоль оси создаёт диагональ (штрих идёт «\» вдоль движения)
      const tail = KSP_HALF_WIDTH_PX * 0.6
      const aX = cx + nx * KSP_HALF_WIDTH_PX - ux * tail
      const aY = cy + ny * KSP_HALF_WIDTH_PX - uy * tail
      const bX = cx - nx * KSP_HALF_WIDTH_PX + ux * tail
      const bY = cy - ny * KSP_HALF_WIDTH_PX + uy * tail
      const a = map.containerPointToLatLng(L.point(aX, aY))
      const b = map.containerPointToLatLng(L.point(bX, bY))
      ticks.push(L.polyline([a, b], opts))
      dist += KSP_TICK_SPACING_PX
    }
    carry = (segLen - (dist - KSP_TICK_SPACING_PX)) % KSP_TICK_SPACING_PX
    if (carry < 0) carry = 0
  }
  return L.layerGroup([outerA, outerB, ...ticks])
}


const mapEl = ref(null)
const mapRef = ref(null)
const markerLayer = ref(null)
let imageBounds = null

let resizeObserver = null
let onWindowResize = null

const initError = ref(null)

// Улитка — состояние zoom-слайдера
const zoomMin = ref(10)
const zoomMax = ref(18)
const zoomValue = ref(14)
const showZoomSlider = ref(true)

function stepZoom(delta) {
  const m = mapRef.value
  if (!m) return
  m.setZoom(m.getZoom() + delta)
}
function onSliderInput(e) {
  const m = mapRef.value
  if (!m) return
  const v = parseFloat(e.target.value)
  if (Number.isFinite(v)) m.setZoom(v)
}

// ======================== УЛИТКА ========================
const ULITKA_LETTERS = ['А','Б','В','Г','Д','Е','Ж','З','И','К','Л','М','Н','О','П','Р','С','Т','У','Ф']
// Показывать координату улитки при движении мыши по карте (может выключить пользователь).
const showUlitka = ref(true)
const showGridCalibration = ref(false)
// Калибровка сетки: смещение и множитель размера клетки.
// Сохраняется в localStorage — по ТЗ п.11 сетка должна совпасть
// с линиями оригинальной карты 1942 г. Разные подложки могут иметь
// разный сдвиг origin, поэтому оставляем настройку пользователю.
const GRID_CAL_KEY = 'saqshy.gridCalibration.v1'
const gridCal = ref((() => {
  try {
    const raw = localStorage.getItem(GRID_CAL_KEY)
    if (raw) return { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, ...JSON.parse(raw) }
  } catch {}
  return { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1 }
})())
let gridLayer = null
let imageBoundsCached = null

function persistGridCalibration() {
  try { localStorage.setItem(GRID_CAL_KEY, JSON.stringify(gridCal.value)) } catch {}
}
function redrawGrid() {
  if (!gridLayer || !imageBoundsCached || !mapRef.value) return
  gridLayer.clearLayers()
  addGrid(mapRef.value, imageBoundsCached, 20, 17, {
    ...gridCal.value,
    layer: gridLayer,
  })
}
function updateGridCal(patch) {
  gridCal.value = { ...gridCal.value, ...patch }
  persistGridCalibration()
  redrawGrid()
}
function resetGridCal() {
  gridCal.value = { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1 }
  persistGridCalibration()
  redrawGrid()
}
// Текущая координата под курсором, например "П12-3".
const currentUlitka = ref('')

/**
 * Возвращает {letter, row, sub, text} для указанного latlng внутри bounds.
 * cols/rows — количество главных квадратов сетки. Улитка = 3×3 подквадратов.
 *
 * Нумерация улитки:
 *   [row 0]  1  2  3     (верхняя строка — 3 квадрата слева-направо)
 *   [row 1]  8  9  4     (средняя — 8 слева, 9 центр, 4 справа)
 *   [row 2]  7  6  5     (нижняя — 7,6,5 слева-направо)
 */
function ulitkaAt(latlng, bounds, cols = 20, rows = 17) {
  const south = bounds.getSouth()
  const west = bounds.getWest()
  const north = bounds.getNorth()
  const east = bounds.getEast()
  const w = east - west
  const h = north - south
  const colW = w / cols
  const rowH = h / rows

  if (latlng.lng < west || latlng.lng > east || latlng.lat < south || latlng.lat > north) {
    return null
  }
  // Индекс главного квадрата
  const ci = Math.min(cols - 1, Math.max(0, Math.floor((latlng.lng - west) / colW)))
  // По вертикали 1 = сверху, rows = снизу (по ТЗ, п.11)
  const yFromTop = north - latlng.lat // 0 = самый верх, h = самый низ
  const rjFromTop = Math.min(rows - 1, Math.max(0, Math.floor(yFromTop / rowH)))
  const rowLabel = rjFromTop + 1 // 1..rows

  // Внутри главного квадрата — подсетка 3×3
  const inCellX = (latlng.lng - (west + ci * colW)) / colW              // 0..1 слева-направо
  const cellTopLat = north - rjFromTop * rowH
  const inCellY = (cellTopLat - latlng.lat) / rowH                       // 0..1 сверху-вниз
  const sx = Math.min(2, Math.max(0, Math.floor(inCellX * 3)))
  const sy = Math.min(2, Math.max(0, Math.floor(inCellY * 3)))

  // Матрица улитки: [sy][sx]
  const ULITKA_MATRIX = [
    [1, 2, 3],
    [8, 9, 4],
    [7, 6, 5],
  ]
  const sub = ULITKA_MATRIX[sy][sx]

  const letter = ULITKA_LETTERS[ci] || String(ci + 1)
  return {
    letter,
    row: rowLabel,
    sub,
    square: `${letter}${rowLabel}`,
    text: `${letter}${rowLabel}-${sub}`,
  }
}

function setupUlitkaTracker(map, bounds, cols = 20, rows = 17) {
  // Всплывающая метка возле курсора
  const badge = L.divIcon({ className: 'ulitka-badge', html: '<span></span>', iconSize: [64, 24] })
  let marker = null

  map.on('mousemove', (e) => {
    if (!showUlitka.value) {
      if (marker) { marker.remove(); marker = null }
      currentUlitka.value = ''
      return
    }
    const coord = ulitkaAt(e.latlng, bounds, cols, rows)
    if (!coord) {
      if (marker) { marker.remove(); marker = null }
      currentUlitka.value = ''
      return
    }
    currentUlitka.value = coord.text
    const html = `<span>${coord.text}</span>`
    if (!marker) {
      marker = L.marker(e.latlng, {
        icon: L.divIcon({ className: 'ulitka-badge', html, iconSize: [80, 24], iconAnchor: [-12, -8] }),
        interactive: false,
        keyboard: false,
      }).addTo(map)
    } else {
      marker.setLatLng(e.latlng)
      const el = marker.getElement()
      if (el) el.innerHTML = html
    }
  })

  map.on('mouseout', () => {
    if (marker) { marker.remove(); marker = null }
    currentUlitka.value = ''
  })

  // Клик по карте (без выбранного инструмента) — всплывающий popup с координатой улитки.
  // По ТЗ (встреча 27.08.2026, стр.13): «когда мы мышкой тыкнули, он должен сразу сказать B12-3».
  map.on('click', (e) => {
    // Пропускаем клик если пользователь рисует / ставит знак — там свои handlers
    if (props.selectedIcon) return
    const coord = ulitkaAt(e.latlng, bounds, cols, rows)
    if (!coord) return
    const html = `
      <div class="ulitka-popup">
        <div class="ulitka-popup__label">Координата (улитка)</div>
        <div class="ulitka-popup__value">${coord.text}</div>
        <div class="ulitka-popup__meta">квадрат ${coord.square} · подквадрат ${coord.sub}</div>
        <button type="button" class="ulitka-popup__copy" data-coord="${coord.text}">Скопировать</button>
      </div>`
    const popup = L.popup({ closeButton: true, autoClose: true, className: 'ulitka-leaflet-popup' })
      .setLatLng(e.latlng)
      .setContent(html)
      .openOn(map)
    // Обработчик кнопки «Скопировать»
    const el = popup.getElement()
    if (el) {
      const btn = el.querySelector('.ulitka-popup__copy')
      if (btn) btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(coord.text)
          btn.textContent = 'Скопировано ✓'
          setTimeout(() => { btn.textContent = 'Скопировать' }, 1500)
        } catch { btn.textContent = 'Не удалось скопировать' }
      })
    }
  })
}

const markersById = new Map()
const selectedMarkerId = ref(null)
let isRightClick = false

const MAX_SAFE = 999.999
let scaleX = 1
let scaleY = 1
let initialZoomLevel = 0

const ICON_FILES = {
  'marker1.svg': require('@/assets/images/map/marker1.svg'),
  'camera.svg':  require('@/assets/images/map/camera.svg'),
  'radar.svg':   require('@/assets/images/map/radar.svg'),
  'vehicle.svg': require('@/assets/images/map/vehicle.svg'),
  'dog.svg':     require('@/assets/images/map/dog.svg'),
  'tower.svg':   require('@/assets/images/map/tower.svg'),
}
const ICON_KEY_TO_FILE = { post: 'marker1.svg', camera: 'camera.svg', radar: 'radar.svg', vehicle: 'vehicle.svg', dog: 'dog.svg', tower: 'tower.svg' }
const PNG_TO_SVG = { 'marker1.png': 'marker1.svg', 'camera.png': 'camera.svg', 'radar.png': 'radar.svg', 'vehicle.png': 'vehicle.svg', 'dog.png': 'dog.svg', 'tower.png': 'tower.svg' }

let MAP_ICONS_CTX
let MAP_ICON_KEYS = new Set()
try {
  MAP_ICONS_CTX = require.context('@/assets/images/map/map_icons', false, /\.(png|jpe?g|svg|bmp)$/i)
  MAP_ICON_KEYS = new Set(MAP_ICONS_CTX.keys().map(k => k.replace('./','')))
} catch (e) {
  // eslint-disable-next-line no-console
  console.warn('map_icons недоступны:', e)
}

// Отдельный набор условных знаков ПС КНБ РК (ключ вида 'pgs_icons/01_zastava.svg')
let PGS_ICONS_CTX
let PGS_ICON_KEYS = new Set()
try {
  PGS_ICONS_CTX = require.context('@/assets/images/map/pgs_icons', false, /\.svg$/i)
  PGS_ICON_KEYS = new Set(PGS_ICONS_CTX.keys().map(k => k.replace('./','')))
} catch (e) {
  console.warn('pgs_icons недоступны:', e)
}

// Полный набор пограничных знаков из docx (ключ вида 'border_signs/N.svg')
let BORDER_SIGNS_CTX
let BORDER_SIGN_KEYS = new Set()
try {
  BORDER_SIGNS_CTX = require.context('@/assets/images/map/border_signs', false, /\.svg$/i)
  BORDER_SIGN_KEYS = new Set(BORDER_SIGNS_CTX.keys().map(k => k.replace('./','')))
} catch (e) {
  console.warn('border_signs недоступны:', e)
}

function resolveIconSrc(nameOrKey){
  if(nameOrKey && nameOrKey.startsWith('text:')) return null
  if(!nameOrKey) return ICON_FILES['marker1.svg']

  // Пограничные знаки из docx — ключ вида "border_signs/N.svg"
  if (typeof nameOrKey === 'string' && nameOrKey.startsWith('border_signs/')) {
    const fname = nameOrKey.slice('border_signs/'.length)
    if (BORDER_SIGNS_CTX && BORDER_SIGN_KEYS.has(fname)) {
      return BORDER_SIGNS_CTX('./' + fname)
    }
    return ICON_FILES['marker1.svg']
  }

  // ПС КНБ РК — ключ вида "pgs_icons/01_zastava.svg"
  if (typeof nameOrKey === 'string' && nameOrKey.startsWith('pgs_icons/')) {
    const fname = nameOrKey.slice('pgs_icons/'.length)
    if (PGS_ICONS_CTX && PGS_ICON_KEYS.has(fname)) {
      return PGS_ICONS_CTX('./' + fname)
    }
    return ICON_FILES['marker1.svg']
  }

  if(/\.(svg|png|jpe?g|bmp)$/i.test(nameOrKey)){
    const fname = nameOrKey
    if (MAP_ICONS_CTX && MAP_ICON_KEYS.has(fname)) {
      return MAP_ICONS_CTX('./' + fname)
    }
    const mapped = PNG_TO_SVG[fname]
    if (ICON_FILES[fname]) return ICON_FILES[fname]
    return ICON_FILES['marker1.svg']
  }

  // Поддержка line: (для превью в списке или легенде - пока возвращаем дефолт, но на карте отрисуется как линия)
  if (nameOrKey.startsWith('line:')) return ICON_FILES['marker1.svg']

  const file = ICON_KEY_TO_FILE[nameOrKey] || 'marker1.svg'
  return ICON_FILES[file] || ICON_FILES['marker1.svg']
}

// === UTILS FOR ENCODING ===
// v2 формат: line:COLOR:TYPE:WEIGHT:PTS
// v1 совместимость: line:COLOR:TYPE:PTS (weight подставляем 3)
function encodeLine(color, type, latlngs, weight) {
  const points = latlngs.map(p => `${Number(p.lat.toFixed(5))},${Number(p.lng.toFixed(5))}`).join(';')
  const w = Number(weight) || 3
  return `line:${color}:${type}:${w}:${points}`
}
function decodeLine(str) {
  const parts = str.split(':')
  if (parts.length < 4) return null
  const color = parts[1]
  const type  = parts[2]
  // Определяем, есть ли поле weight (число) или сразу точки
  let weight = 3
  let pointsRaw
  const maybeWeight = parts[3]
  if (parts.length >= 5 && /^\d+(?:\.\d+)?$/.test(maybeWeight)) {
    weight = Number(maybeWeight) || 3
    pointsRaw = parts.slice(4).join(':')
  } else {
    pointsRaw = parts.slice(3).join(':')
  }
  const points = pointsRaw.split(';').map(p => {
    const [lat, lng] = p.split(',').map(Number)
    return { lat, lng }
  })
  return { color, type, weight, points }
}

// === ОБЛАСТИ (полигон / круг / прямоугольник) ===
// По ТЗ встречи 03.09.2026: заливка с прозрачностью ~35%, у границы — тип линии
// и толщина. Формат v2 (единый для всех областей, геометрия — в хвосте):
//   polygon:STROKE:FILL:PATTERN:DASH:WEIGHT:LAT,LNG;LAT,LNG;…
//   circle:STROKE:FILL:PATTERN:DASH:WEIGHT:CLAT,CLNG:RADIUS_M
//   rect:STROKE:FILL:PATTERN:DASH:WEIGHT:SWLAT,SWLNG:NELAT,NELNG
// v1 (читаем для старых записей):
//   polygon:STROKE:FILL:PATTERN:POINTS   circle:COLOR:C:R   rect:COLOR:SW:NE
const AREA_FILL_OPACITY = 0.35
const AREA_HATCH_OPACITY = 0.85
const AREA_DEFAULT_WEIGHT = 2
const AREA_DASH = { solid: null, dashed: '10 6', dotted: '2 6', dashdot: '10 4 2 4' }

// Прозрачность задаётся только через fillOpacity: альфа из старых '#RRGGBB50'
// отбрасывается, иначе она перемножалась с fillOpacity (выходило ~16%).
function normalizeFill(fill) {
  if (!fill || fill === 'transparent' || fill === 'none') return null
  const m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(fill)
  return m ? `#${m[1]}` : fill
}

function areaStyle({ stroke, fill, pattern, dash, weight }) {
  const fillColor = normalizeFill(fill)
  const isHatched = pattern === 'hatched'
  const style = {
    color: stroke,
    weight: Number(weight) || AREA_DEFAULT_WEIGHT,
    fill: !!fillColor || isHatched,
    fillColor: fillColor || stroke,
    fillOpacity: isHatched ? AREA_HATCH_OPACITY : AREA_FILL_OPACITY,
    className: isHatched ? 'hatched-fill' : '',
  }
  if (AREA_DASH[dash]) style.dashArray = AREA_DASH[dash]
  if (dash === 'dotted') style.lineCap = 'round'
  return style
}

function encodeArea(kind, { stroke, fill, pattern, dash, weight }, geometry) {
  const f = normalizeFill(fill) || 'transparent'
  const w = Number(weight) || AREA_DEFAULT_WEIGHT
  return `${kind}:${stroke}:${f}:${pattern || 'solid'}:${dash || 'solid'}:${w}:${geometry}`
}

function parseLatLngList(raw) {
  return String(raw || '').split(';').filter(Boolean).map(p => {
    const [lat, lng] = p.split(',').map(Number)
    return { lat, lng }
  })
}

// → { kind, style: {stroke, fill, pattern, dash, weight}, geom: [...] } | null
function decodeArea(str) {
  const parts = String(str || '').split(':')
  const kind = parts[0]
  const v2Len = kind === 'polygon' ? 7 : 8
  if (parts.length === v2Len) {
    const [, stroke, fill, pattern, dash, weight, ...geom] = parts
    return { kind, style: { stroke, fill, pattern, dash, weight: Number(weight) }, geom }
  }
  if (kind === 'polygon' && parts.length === 5) {
    const [, stroke, fill, pattern, pts] = parts
    return { kind, style: { stroke, fill, pattern, dash: 'solid', weight: AREA_DEFAULT_WEIGHT }, geom: [pts] }
  }
  if ((kind === 'circle' || kind === 'rect') && parts.length === 4) {
    const [, color, a, b] = parts
    return { kind, style: { stroke: color, fill: color, pattern: 'solid', dash: 'solid', weight: AREA_DEFAULT_WEIGHT }, geom: [a, b] }
  }
  return null
}

// Текущие настройки области из сайдбара
function currentAreaStyle() {
  return {
    stroke: props.selectedColor || '#000000',
    fill: props.fillColor,
    pattern: props.fillPattern || 'solid',
    dash: props.areaDash || 'solid',
    weight: props.strokeWidth || AREA_DEFAULT_WEIGHT,
  }
}


const mapImageUrl = require('../../assets/images/map/maps/mapdps1.png')

async function waitForContainerSize(el, { tries = 20, delayMs = 50 } = {}) {
  for (let i = 0; i < tries; i++) {
    if (el && el.clientWidth > 0 && el.clientHeight > 0) return true
    await new Promise((r) => setTimeout(r, delayMs))
  }
  return !!el
}

onMounted(async () => {
  try {
    await nextTick()
    const imageUrl = mapImageUrl
    const center = [43.021481, 74.703937]

    // Дождёмся decode() чтобы Leaflet отдал уже готовое изображение, а не блокировал UI
    // на разбор большого PNG. При первом заходе из main.js уже мог начаться preload,
    // на повторном рендере — карта уже в HTTP-кэше и decode пройдёт мгновенно.
    try {
      const entry = preloadImage(imageUrl)
      if (entry && entry.ready) await entry.ready
    } catch { /* тихо: если не смогли — Leaflet сам загрузит */ }

    const mapContainer = mapEl.value
    if (!mapContainer) {
      console.error('Map container not found')
      return
    }

    // If mounted inside a flex layout, the container can be 0x0 for a moment.
    // Wait a bit so Leaflet doesn't initialize on a zero-sized element.
    await waitForContainerSize(mapContainer)

    const map = L.map(mapContainer, {
      zoomControl: true,
      minZoom: 10,
      maxZoom: 18,
      zoomSnap: 0.5,
    }).setView(center, 14)

    // Синхронизация "улитки" (внешнего вертикального слайдера) с zoom карты
    zoomMin.value = map.getMinZoom()
    zoomMax.value = map.getMaxZoom()
    zoomValue.value = map.getZoom()
    map.on('zoomend', () => { zoomValue.value = map.getZoom() })

    // Keep Leaflet sized correctly in flex / resizable containers.
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        try { map.invalidateSize(false) } catch {}
      })
      try { resizeObserver.observe(mapContainer) } catch {}
    } else {
      onWindowResize = () => {
        try { map.invalidateSize(false) } catch {}
      }
      window.addEventListener('resize', onWindowResize)
    }

    const latOffset = 0.05
    const lngOffset = 0.08
    const bounds = L.latLngBounds([
      [center[0] - latOffset, center[1] - lngOffset],
      [center[0] + latOffset, center[1] + lngOffset]
    ])
    
    L.imageOverlay(imageUrl, bounds, { 
      opacity: 1, 
      interactive: false 
    }).addTo(map)

    mapRef.value = map
    imageBounds = bounds
    markerLayer.value = L.layerGroup().addTo(map)

    map.fitBounds(bounds, { animate: false })

    // In flex layouts Leaflet may render blank until size is invalidated.
    // Force a size recalculation after the DOM settles.
    setTimeout(() => {
      try { map.invalidateSize(true) } catch {}
    }, 0)

    const initialZoom = map.getZoom()
    // Show a bit more of the map by default (previously we forced a zoom-in step).
    initialZoomLevel = initialZoom
    
    map.setMaxBounds(bounds)
    map.options.maxBoundsViscosity = 1.0
    map.setMinZoom(initialZoom)
    map.setZoom(initialZoom)

    map.dragging.enable()
    map.scrollWheelZoom.enable()
    map.touchZoom.enable()
    map.doubleClickZoom.enable()
    map.boxZoom.enable()
    map.keyboard.enable()

    gridLayer = L.layerGroup().addTo(map)
    imageBoundsCached = bounds
    redrawGrid()

    // ============ УЛИТКА (координатная сетка B12-3) ============
    // Каждый квадрат сетки 20×17 делится на 9 подквадратов 3×3.
    // Нумерация подквадратов по спирали (улитка):
    //   1 2 3
    //   8 9 4      (центр = 9, обход по часовой стрелке от левого-верхнего)
    //   7 6 5
    setupUlitkaTracker(map, bounds, 20, 17)

    const east = imageBounds.getEast()
    const north = imageBounds.getNorth()
    scaleX = east > MAX_SAFE ? (MAX_SAFE / east) : 1
    scaleY = north > MAX_SAFE ? (MAX_SAFE / north) : 1

    try {
      const existingResp = await listAllMapMarkers().catch(() => [])
      const existing = Array.isArray(existingResp)
        ? existingResp
        : (Array.isArray(existingResp?.results) ? existingResp.results : [])
      existing.forEach(drawExistingMarker)
    } catch(e){ console.warn('Load markers failed', e) }

    // Обновляем размер маркеров плавно: во время анимации зума —
    // синхронное transform: scale() через zoomanim, а точное пересоздание
    // divIcon только один раз по zoomend (иначе setIcon() дёргает DOM 60 раз/сек и лагает).
    map.on('zoomanim', onZoomAnim)
    map.on('zoomend', updateAllMarkersSize)

    if (!map.getPane('doubleLineInner')) {
        map.createPane('doubleLineInner')
        map.getPane('doubleLineInner').style.zIndex = 620
        map.getPane('doubleLineInner').style.pointerEvents = 'none'
    }
  } catch (error) {
    initError.value = error
    console.error('Map initialization error:', error)
  }
})

function onMapContextMenu(e) {
  if (isDrawing || isDrawingPolygon) {
    L.DomEvent.preventDefault(e)
    forceFinish()
  }
}

let attachInterval = null


// Размер знака на исходном зуме. По встрече 03.09.2026: 36px было слишком крупно
// для карты 1:200 000 — знак должен быть «маленькой точкой» и расти вместе с картой.
const ICON_BASE_PX = 22
const FLEX_ICON_FACTOR = 1.2

function getCurrentIconSize() {
  if (!mapRef.value) return ICON_BASE_PX
  const zoom = mapRef.value.getZoom()
  const scale = Math.pow(2, zoom - initialZoomLevel)
  return ICON_BASE_PX * scale
}

// По ТЗ 03.09.2026: SVG-знаки из docx часто имеют широкий aspect (200×27 → 7:1)
// — в квадрате они превращаются в тонкую полоску, глазом почти не видно.
// Возвращаем {w, h} с учётом aspect. Для флах-знаков базовый размер =
// FLEX_ICON_FACTOR × обычного маркера (масштабируется вместе с картой).
const _iconAspectCache = new Map()
function iconSizeForKey(nameOrKey) {
  const base = getCurrentIconSize()
  if (!nameOrKey || typeof nameOrKey !== 'string') return { w: base, h: base }
  if (!nameOrKey.startsWith('border_signs/')) return { w: base, h: base }
  const flexBase = base * FLEX_ICON_FACTOR
  const aspect = _iconAspectCache.get(nameOrKey)
  if (aspect == null) {
    // Ленивая загрузка: сначала кладём квадрат, параллельно замеряем через <img>.
    // После загрузки — пересоздаём все маркеры с этим ключом, чтобы обновить aspect.
    const src = resolveIconSrc(nameOrKey)
    const img = new Image()
    img.onload = () => {
      const a = (img.naturalWidth || 1) / (img.naturalHeight || 1)
      _iconAspectCache.set(nameOrKey, a)
      // Перерисовываем существующие маркеры с этим nameOrKey.
      try {
        markersById.forEach((m) => {
          const imgName = m?.options?.imgName
          if (m && m.setIcon && (imgName === nameOrKey || parseEl(imgName)?.key === nameOrKey)) {
            updateMarkerIcon(m, imgName, false)
          }
        })
      } catch (_) { /* silent */ }
    }
    img.src = src
    _iconAspectCache.set(nameOrKey, 1)
    return { w: flexBase, h: flexBase }
  }
  // Ограничиваем ширину: max aspect 8:1, min 0.25 (высокие знаки тоже ок).
  const a = Math.min(Math.max(aspect, 0.25), 8)
  if (a >= 1) return { w: Math.round(flexBase * a), h: Math.round(flexBase) }
  return { w: Math.round(flexBase), h: Math.round(flexBase / a) }
}

// Плавное масштабирование маркеров через CSS-переменную --marker-scale на контейнере карты.
// Каждый маркер использует var(--marker-scale) для transform. Leaflet свой transform ставит
// на .leaflet-marker-icon, а мы масштабируем .map-marker__wrap (inner) — они не конфликтуют.
// При zoomanim ставим целевой factor, а по zoomend — factor 1 (базовый размер уже пересчитан).
function onZoomAnim(e) {
  const currentZoom = mapRef.value?.getZoom() ?? initialZoomLevel
  const targetZoom = e.zoom
  const factor = Math.pow(2, targetZoom - currentZoom)
  const container = mapRef.value?.getContainer()
  if (container) {
    container.style.setProperty('--marker-scale', String(factor))
    // Duration совпадает с Leaflet-анимацией zoom (по умолчанию 0.25s)
    container.style.setProperty('--marker-scale-duration', '0.25s')
  }
}

function updateAllMarkersSize() {
  const container = mapRef.value?.getContainer()
  if (container) {
    // Отключаем transition чтобы моментально сбросить scale к 1
    container.style.setProperty('--marker-scale-duration', '0s')
    container.style.setProperty('--marker-scale', '1')
  }
  // Пересчитываем реальные размеры divIcon
  updateAllMarkersSizeImmediate()
}

function updateAllMarkersSizeImmediate() {
  markersById.forEach((marker, id) => {
    // If it's a layer group (double line), we don't resize it like an icon
    if (marker instanceof L.LayerGroup) return

    // Also skip polylines
    if (marker instanceof L.Polyline) return

    // Вектора (круг/полигон) и прочие слои без setIcon не трогаем
    if (!marker.setIcon) return
    updateMarkerIcon(marker, marker.options.imgName, id === selectedMarkerId.value)
  })
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, ch =>
    ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[ch]))
}

function markerHtml(src, nameOrKey, showDelete, size = ICON_BASE_PX, caption = '', dims = null) {
  if (nameOrKey && nameOrKey.startsWith('text:')) {
    // text:COLOR:CONTENT or text:CONTENT (legacy)
    let text = nameOrKey.substring(5)
    let color = 'red'
    if (text.startsWith('#')) {
       const match = text.match(/^(#[A-Fa-f0-9]{6}):(.*)/)
       if (match) {
         color = match[1]
         text = match[2]
       }
    }

    return `<div class="map-marker__wrap" style="position:relative;">
      <div style="font-size: 14px; font-weight: bold; white-space: nowrap; color: ${color}; text-shadow: 1px 1px 0 #fff; padding: 2px 4px; border: 1px dashed transparent;">${text}</div>
      ${showDelete ? `<button class="map-marker__delete" type="button" title="Удалить" data-role="delete-btn" style="top:-8px; right:-8px;">×</button>` : ''}
    </div>`
  }
  if (nameOrKey && nameOrKey.startsWith('line:')) {
     return `<div style="display:none"></div>`
  }

  const captionHtml = caption
    ? `<div class="map-marker__caption" title="${escapeHtml(caption)}">${escapeHtml(caption)}</div>`
    : ''

  // По ТЗ 03.09.2026: для широких/узких SVG (flex-знаки) используем реальные
  // aspect-размеры вместо квадрата, чтобы полосы/знаки были видны.
  const w = (dims && dims.w) || size
  const h = (dims && dims.h) || size
  // Кнопка «×» — фиксированного размера (CSS .map-marker__delete), не растёт с зумом.
  return `<div class="map-marker__wrap" style="width:${w}px; height:${h}px;">
    <img src="${src}" alt="${nameOrKey || ''}" style="width: 100%; height: 100%; object-fit: contain;" />
    ${captionHtml}
    ${showDelete ? `<button class="map-marker__delete" type="button" title="Удалить" data-role="delete-btn">×</button>` : ''}
  </div>`
}

// === ЭЛЕМЕНТЫ ЗНАКОВ (поворот / размер / прилипание к линии) ===
// По встрече 03.09.2026: гибкие знаки собираются из элементов, которые ставятся
// на любое место линии, вокруг неё или в неё, поворачиваются и масштабируются.
// Формат img_name:  el:KEY:COLOR:SX:ROT:SY
//   KEY — `lib.<key>` (библиотека lineElements.js) или ключ файла-иконки
//         (pgs_icons/…, border_signs/…) — у файлов COLOR = '-';
//   SX / SY — растяжение по ширине / высоте знака (1 = базовый размер);
//   ROT — поворот в градусах. Растяжение — в осях самого знака (до поворота).
// SY необязателен: запись el:KEY:COLOR:SCALE:ROT = равномерный масштаб.
// Старые записи без префикса (просто ключ файла) остаются как есть и
// переводятся в el: при первом повороте/масштабировании.
const EL_SCALE_MIN = 0.3
const EL_SCALE_MAX = 5

const clampScale = (v) => Math.min(Math.max(Number(v) || 1, EL_SCALE_MIN), EL_SCALE_MAX)
const normRot = (v) => ((Math.round(Number(v) || 0) % 360) + 360) % 360

function isLibKey(key) { return typeof key === 'string' && key.startsWith('lib.') }

function parseEl(nameOrKey) {
  if (typeof nameOrKey !== 'string' || !nameOrKey.startsWith('el:')) return null
  const parts = nameOrKey.split(':')
  if (parts.length < 5) return null
  const [, key, color, sx, rot, sy] = parts
  return {
    key,
    color: color === '-' ? null : color,
    sx: clampScale(sx),
    sy: clampScale(sy ?? sx),
    rot: normRot(rot),
  }
}

function encodeEl({ key, color, sx, sy, rot }) {
  const c = isLibKey(key) ? (color || '#000000') : '-'
  const x = Number(clampScale(sx).toFixed(2))
  const y = Number(clampScale(sy ?? sx).toFixed(2))
  return `el:${key}:${c}:${x}:${normRot(rot)}${y !== x ? `:${y}` : ''}`
}

const isStretched = (el) => Math.abs((el.sx ?? 1) - (el.sy ?? 1)) > 0.001

// Размер (px на текущем зуме) и точка привязки элемента.
function elementGeometry(el) {
  const sx = el.sx ?? 1, sy = el.sy ?? 1
  if (isLibKey(el.key)) {
    const def = getLineElement(el.key)
    const vw = def?.w || 48, vh = def?.h || 48
    const k = getCurrentIconSize() / 48
    const [ax, ay] = def?.anchor || [vw / 2, vh / 2]
    return { w: vw * k * sx, h: vh * k * sy, ax: ax * k * sx, ay: ay * k * sy }
  }
  const { w, h } = iconSizeForKey(el.key)
  return { w: w * sx, h: h * sy, ax: (w * sx) / 2, ay: (h * sy) / 2 }
}

function elementInnerHtml(el) {
  const stretch = isStretched(el)
  if (isLibKey(el.key)) {
    const def = getLineElement(el.key)
    return def ? lineElementSvg(def, { color: el.color || '#000000', stretch }) : ''
  }
  return `<img src="${resolveIconSrc(el.key)}" alt="" style="width:100%;height:100%;object-fit:${stretch ? 'fill' : 'contain'};display:block" />`
}

// Обёртка .map-marker__wrap не поворачивается (подпись и «×» остаются
// горизонтальными), поворачивается только .map-el вокруг точки привязки.
function elementHtml(el, showDelete, caption) {
  const { w, h, ax, ay } = elementGeometry(el)
  const captionHtml = caption
    ? `<div class="map-marker__caption" title="${escapeHtml(caption)}">${escapeHtml(caption)}</div>`
    : ''
  // У выделенного знака — рамка и 4 ручки растяжения (вверх/вниз/влево/вправо).
  // Ручки внутри .map-el, поэтому поворачиваются вместе со знаком.
  const handles = showDelete
    ? '<span class="map-el__frame"></span>' +
      ['n', 's', 'e', 'w'].map(d => `<span class="map-el__handle map-el__handle--${d}" data-handle="${d}" title="Растянуть"></span>`).join('')
    : ''
  return `<div class="map-marker__wrap" style="width:${w}px;height:${h}px;transform-origin:${ax}px ${ay}px">
    <div class="map-el" style="width:${w}px;height:${h}px;transform-origin:${ax}px ${ay}px;transform:rotate(${el.rot}deg)">${elementInnerHtml(el)}${handles}</div>
    ${captionHtml}
    ${showDelete ? `<button class="map-marker__delete" type="button" title="Удалить" data-role="delete-btn">×</button>` : ''}
  </div>`
}

// Единая сборка divIcon для любого точечного знака (иконка, текст, элемент).
// Файловые иконки старого формата рисуются как элемент без поворота/растяжения —
// так у выделенной иконки тоже есть ручки.
function buildIcon(nameOrKey, showDelete, caption = '') {
  const el = parseEl(nameOrKey) || (isPlaceableKey(nameOrKey) ? { key: nameOrKey, color: null, sx: 1, sy: 1, rot: 0 } : null)
  if (el) {
    const { w, h, ax, ay } = elementGeometry(el)
    return L.divIcon({ className: 'map-marker', html: elementHtml(el, showDelete, caption), iconSize: [w, h], iconAnchor: [ax, ay] })
  }
  const src = resolveIconSrc(nameOrKey)
  const { w, h } = iconSizeForKey(nameOrKey)
  return L.divIcon({
    className: 'map-marker',
    html: markerHtml(src, nameOrKey, showDelete, Math.max(w, h), caption, { w, h }),
    iconSize: [w, h],
    iconAnchor: [w/2, h/2],
  })
}

function updateMarkerIcon(marker, nameOrKey, showDelete) {
  marker.setIcon(buildIcon(nameOrKey, showDelete, marker.options.caption || ''))
}

function setSelectedMarker(id) {
  selectedMarkerId.value = id
  selectedEl.value = id != null ? elStateFor(id, markersById.get(id)) : null
  markersById.forEach((m, mid) => {
    // Check if it's a marker that supports setting icon
    if (m.setIcon) {
       const nameOrKey = m.options.imgName || m.options.iconKey || 'marker1.svg'
       updateMarkerIcon(m, nameOrKey, mid === id)
       if (mid === id && m.setZIndexOffset) m.setZIndexOffset(1000)
       else if (m.setZIndexOffset) m.setZIndexOffset(0)
    }
  })

}

const editorsByLayerId = new Map() // markerId -> { cleanup() }

function cleanupEditor(id) {
  if (editorsByLayerId.has(id)) {
      editorsByLayerId.get(id).cleanup()
      editorsByLayerId.delete(id)
  }
}

function drawExistingMarker(rec){
  if(!markerLayer.value) return
  const rawLat = Number(parseFloat(rec.lat).toFixed(6))
  const rawLng = Number(parseFloat(rec.lng).toFixed(6))
  if(!isFinite(rawLat) || !isFinite(rawLng)) return
  if(!imageBounds) return

  const south = imageBounds.getSouth()
  const west = imageBounds.getWest()
  const north = imageBounds.getNorth()
  const east = imageBounds.getEast()

  let lat = rawLat
  let lng = rawLng
  const needsUnscale = (east > MAX_SAFE || north > MAX_SAFE) && rawLat <= MAX_SAFE && rawLng <= MAX_SAFE
  if (needsUnscale) {
    lat = rawLat / (scaleY || 1)
    lng = rawLng / (scaleX || 1)
  }

  if(lat < south || lat > north || lng < west || lng > east) return

  const nameOrKey = rec.img_name || rec.key || rec.icon_key
  
  if (nameOrKey && nameOrKey.startsWith('line:')) {
      // Draw Polyline
      const decoded = decodeLine(nameOrKey)
      if (decoded && decoded.points.length) {
         // Scale points back to map coords
         // ... (existing comments)
         
         const pts = decoded.points.map(p => {
             // unscale
             let plat = p.lat
             let plng = p.lng
             if (needsUnscale) {
                plat = plat / (scaleY || 1)
                plng = plng / (scaleX || 1)
             }
             return [plat, plng]
         })
         
         // === Формируем стиль по type + weight ===
         const t = decoded.type
         const baseWeight = decoded.weight || 3
         // Пресеты dashArray для каждого типа
         const DASH = {
           solid: null, freehand: null, thick: null,
           dashed: '10 6', dotted: '2 6', dashdot: '10 4 2 4',
           double: null, double_dashed: '8 4',
           arrow: null, arrow_double: null,
           ksp: '6 6', band: null, band_dashed: '18 10', band_dotted: '2 10',
           fence: null,
         }
         const isDouble = t === 'double' || t === 'double_dashed' || t === 'arrow_double'
         const isArrow = t === 'arrow' || t === 'arrow_double'
         // По ТЗ 03.09.2026: цвет КСП/полос теперь берётся из decoded.color
         // (selectedColor пользователя), а не хардкодится чёрным.
         const drawColor = decoded.color

         if (isDouble) {
             const dash = DASH[t]
             // По ТЗ 03.09.2026: две ПАРАЛЛЕЛЬНЫЕ линии того же цвета,
             // сдвинутые по нормали на DOUBLE_OFFSET_PX. Раньше рисовалась
             // «широкая цветная + белая внутри» — визуально одна линия.
             const ptsLL = pts.map(p => L.latLng(p[0], p[1]))
             const { left, right } = parallelOffsetPoints(mapRef.value, ptsLL, DOUBLE_OFFSET_PX)
             const baseOpts = {
               color: drawColor,
               weight: baseWeight,
               ...(dash ? { dashArray: dash } : {}),
             }
             const leftLine = L.polyline(left, baseOpts)
             const rightLine = L.polyline(right, baseOpts)
             const group = L.layerGroup([leftLine, rightLine]).addTo(markerLayer.value)
             leftLine.options.markerId = rec.id
             rightLine.options.markerId = rec.id
             leftLine.on('click', e => onMarkerClick(e.originalEvent, leftLine))
             rightLine.on('click', e => onMarkerClick(e.originalEvent, rightLine))
             group.options = { markerId: rec.id, imgName: nameOrKey }
             group._doubleBasePts = ptsLL
             markersById.set(rec.id, group)
             if (isArrow) attachArrowMarker(group, pts, drawColor)
             // По ТЗ 03.09.2026 (баг «синие точки над знаками» + «не сохраняет»):
             // vertex-ручки редактора рисовали синие circleMarker на каждой точке
             // сохранённой линии, а для layerGroup ещё и падали на getLatLngs()
             // — из-за исключения save-обработчик считал попытку неудачной.
             // Отключаем ручки для сохранённых линий.
             // if (!props.readOnly) createEditorForLayer(group, rec.id, false)
             return
         }

         // По ТЗ 03.09.2026: opacity/smoothFactor как в live-режиме — иначе
         // после save линия выглядела чуть жирнее чем во время рисования.
         const opts = { color: drawColor, weight: baseWeight, opacity: 0.95, smoothFactor: 1 }
         if (DASH[t]) opts.dashArray = DASH[t]
         if (t === 'thick') opts.weight = baseWeight + 3
         if (t === 'dotted') opts.lineCap = 'round'
         // По ТЗ 03.09.2026: «обычные полосы» — просто жирный stroke.
         // dashArray даёт эффект штрихов/шпал/точек вдоль широкой полосы.
         if (t === 'band' || t === 'band_dashed' || t === 'band_dotted') {
           opts.weight = baseWeight + 8
           opts.lineCap = t === 'band_dotted' ? 'round' : 'butt'
         }

         // По ТЗ 03.09.2026 (баг «КСП рисуется одной лентой»): рендер через
         // layerGroup (2 полосы + штрихи) — надёжнее pattern-stroke.
         if (t === 'ksp') {
           const ptsLL = pts.map(p => L.latLng(p[0], p[1]))
           const group = buildKspLayer(mapRef.value, ptsLL, drawColor, baseWeight)
           group.addTo(markerLayer.value)
           group.options = { markerId: rec.id, imgName: nameOrKey }
           group.eachLayer(l => {
             l.options.markerId = rec.id
             l.on('click', e => onMarkerClick(e.originalEvent, l))
           })
           markersById.set(rec.id, group)
           // Vertex-ручки отключены (баг «синие точки» — см. isDouble выше).
           return
         }

         const poly = L.polyline(pts, opts).addTo(markerLayer.value)
         if (isArrow) attachArrowMarker(poly, pts, drawColor)
         // Store meta
         poly.options.markerId = rec.id
         poly.options.imgName = nameOrKey
         poly.on('click', e => onMarkerClick(e.originalEvent, e.target))
         // Polylines don't support dragend out of the box easily without editing tools, 
         // but we can at least support delete.
         
         
         markersById.set(rec.id, poly)
         // Vertex-ручки отключены (см. комментарий выше в ветке isDouble).
         return
      }
  }

  if (nameOrKey && /^(polygon|circle|rect):/.test(nameOrKey)) {
      const area = decodeArea(nameOrKey)
      const unscale = ({ lat, lng }) => needsUnscale
        ? { lat: lat / (scaleY || 1), lng: lng / (scaleX || 1) }
        : { lat, lng }
      let layer = null
      if (area) {
        const style = areaStyle(area.style)
        if (area.kind === 'circle') {
          const [c] = parseLatLngList(area.geom[0])
          const radius = Number(area.geom[1])
          if (c && isFinite(c.lat) && isFinite(c.lng) && isFinite(radius) && radius > 0) {
            const p = unscale(c)
            layer = L.circle([p.lat, p.lng], { ...style, radius })
          }
        } else if (area.kind === 'rect') {
          const [sw] = parseLatLngList(area.geom[0])
          const [ne] = parseLatLngList(area.geom[1])
          if (sw && ne && [sw.lat, sw.lng, ne.lat, ne.lng].every(isFinite)) {
            const s = unscale(sw), n = unscale(ne)
            layer = L.rectangle([[s.lat, s.lng], [n.lat, n.lng]], style)
          }
        } else {
          const pts = parseLatLngList(area.geom[0]).map(unscale)
          if (pts.length >= 3) layer = L.polygon(pts.map(p => [p.lat, p.lng]), style)
        }
      }
      if (layer) {
        layer.addTo(markerLayer.value)
        if (area.style.pattern === 'hatched') applyHatchedFill(layer, area.style.stroke)
        layer.options.markerId = rec.id
        layer.options.imgName = nameOrKey
        layer.on('click', e => onMarkerClick(e.originalEvent, e.target))
        markersById.set(rec.id, layer)
        // Vertex-ручки отключены (баг «синие точки»).
        return
      }
  }

  const caption = rec.caption || ''
  const icon = buildIcon(nameOrKey, false, caption)
  const m = L.marker([lat,lng], { icon, draggable: !props.readOnly })
  m.options.markerId = rec.id
  m.options.imgName = nameOrKey
  m.options.caption = caption
  m.on('click', e => onMarkerClick(e.originalEvent, e.target))
  m.on('dragend', e => onMarkerDragEnd(e.target))
  m.on('contextmenu', e => onMarkerContext(e))
  m.addTo(markerLayer.value)
  markersById.set(rec.id, m)
}





function addMarkerAt(latlng, nameOrFile, id, caption = ''){
  if(!markerLayer.value) return
  const icon = buildIcon(nameOrFile, false, caption)
  const m = L.marker([latlng.lat, latlng.lng], { icon, draggable: !props.readOnly })
  if(id) m.options.markerId = id
  m.options.imgName = nameOrFile
  m.options.caption = caption
  m.on('click', e => onMarkerClick(e.originalEvent, e.target))
  m.on('dragend', e => onMarkerDragEnd(e.target))
  m.on('contextmenu', e => onMarkerContext(e))
  m.addTo(markerLayer.value)
  if (id) markersById.set(id, m)
}

// Правый клик по маркеру — редактирование подписи (номер / состав / время).
async function onMarkerContext(e) {
  if (props.readOnly) return
  L.DomEvent.preventDefault(e.originalEvent)
  L.DomEvent.stopPropagation(e.originalEvent)
  const marker = e.target
  const id = marker?.options?.markerId
  if (!id) return
  const current = marker.options.caption || ''
  const next = window.prompt('Подпись к знаку (номер / состав / время):', current)
  if (next === null) return
  const caption = next.trim().slice(0, 240)
  try {
    await updateMapMarker(id, { caption })
    marker.options.caption = caption
    const nameOrKey = marker.options.imgName || marker.options.iconKey || 'marker1.svg'
    updateMarkerIcon(marker, nameOrKey, id === selectedMarkerId.value)
  } catch (err) { console.error('update caption failed', err) }
}

async function onMarkerClick(domEvent, marker){
  if (props.readOnly) return  // ← Добавить эту строку в начало

  // Check erasure
  if (props.selectedIcon === 'draw_eraser') {
     const id = marker?.options?.markerId
     if (id) {
         emit('request-delete-marker', id)
         return
     }
  }

  const target = domEvent?.target
  const id = marker?.options?.markerId
  if (!id) return

  if (target && target.closest && target.closest('[data-role="delete-btn"]')) {
    emit('request-delete-marker', id)
    return
  }

  setSelectedMarker(id)
}


async function deleteMarkerById(id) {
  const marker = markersById.get(id)
  if (!marker) return
  try {
    await deleteMapMarker(id)
    if (markerLayer.value) markerLayer.value.removeLayer(marker)

    markersById.delete(id)
    cleanupEditor(id) // Remove handles
    if (selectedMarkerId.value === id) {
      selectedMarkerId.value = null
      selectedEl.value = null
    }
    emit('marker-deleted', id)
  } catch (e) {
    console.error('Delete marker failed', e)
  }
}

function invalidateSize() {
  const map = mapRef.value
  if (!map) return
  try { map.invalidateSize(true) } catch {}
}

function fitToImage() {
  const map = mapRef.value
  if (!map || !imageBounds) return
  try {
    map.invalidateSize(true)
    map.fitBounds(imageBounds, { animate: false })
  } catch {}
}

defineExpose({ deleteMarkerById, forceFinish, invalidateSize, fitToImage, undoLast, redoLast, historyState })

// Ctrl+Z / Ctrl+Y (Cmd+Z / Cmd+Shift+Z на macOS) — глобальный undo/redo
function onGlobalKeydown(e) {
  if (props.readOnly) return
  const meta = e.ctrlKey || e.metaKey
  if (!meta) {
    onSelectedElementKey(e)
    return
  }
  const key = (e.key || '').toLowerCase()
  if (key === 'z' && !e.shiftKey) {
    e.preventDefault()
    undoLast()
  } else if ((key === 'y') || (key === 'z' && e.shiftKey)) {
    e.preventDefault()
    redoLast()
  }
}
// Горячие клавиши для выделенного знака: R / Shift+R — поворот ±15°,
// + / − — размер, Delete / Backspace — удалить, Esc — снять выделение.
function onSelectedElementKey(e) {
  const s = selectedEl.value
  if (!s || e.altKey) return
  const tag = (e.target?.tagName || '').toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return
  const key = e.key
  if (key === 'r' || key === 'R' || key === 'к' || key === 'К') applyElChange({ rot: s.rot + (e.shiftKey ? -15 : 15) })
  else if (key === '+' || key === '=') resizeElUniform(1.15)
  else if (key === '-' || key === '_') resizeElUniform(1 / 1.15)
  else if (key === 'Delete' || key === 'Backspace') emit('request-delete-marker', s.id)
  else if (key === 'Escape') setSelectedMarker(null)
  else return
  e.preventDefault()
}

onMounted(() => { window.addEventListener('keydown', onGlobalKeydown) })
onUnmounted(() => { window.removeEventListener('keydown', onGlobalKeydown) })

function forceFinish() {
    if (isDrawing) finishDrawing()
    if (isDrawingPolygon) finishPolygon()
}

async function onMarkerDragEnd(marker){
  if (props.readOnly) return  // ← Добавить эту строку
  
  const id = marker?.options?.markerId
  if(!id) return
  const pos = marker.getLatLng()
  let lat = Number(pos.lat.toFixed(6))
  let lng = Number(pos.lng.toFixed(6))

  if(imageBounds){
    const south = imageBounds.getSouth()
    const west = imageBounds.getWest()
    const north = imageBounds.getNorth()
    const east = imageBounds.getEast()
    lat = Math.min(Math.max(lat, south), north)
    lng = Math.min(Math.max(lng, west), east)
    marker.setLatLng([lat,lng])
  }

  // Перетащили знак к линии — прилипает к ней (элементы библиотеки ещё и
  // разворачиваются вдоль линии).
  const payload = {}
  const imgName = marker.options.imgName
  const el = parseEl(imgName)
  if (props.snapToLine && marker.setIcon && (el || isPlaceableKey(imgName))) {
    const key = el ? el.key : imgName
    const place = computePlacement(L.latLng(lat, lng), key, el ? el.rot : 0, el ? el.sy : 1, id)
    if (place.snapped) {
      lat = Number(place.latlng.lat.toFixed(6))
      lng = Number(place.latlng.lng.toFixed(6))
      marker.setLatLng([lat, lng])
      const next = encodeEl({ key, color: el?.color || props.selectedColor, sx: el ? el.sx : 1, sy: el ? el.sy : 1, rot: place.rot })
      if (next !== imgName) {
        payload.img_name = next
        marker.options.imgName = next
        updateMarkerIcon(marker, next, id === selectedMarkerId.value)
        if (selectedEl.value?.id === id) selectedEl.value = elStateFor(id, marker)
      }
    }
  }

  const saveLat = Number((lat * (scaleY || 1)).toFixed(6))
  const saveLng = Number((lng * (scaleX || 1)).toFixed(6))
  try { await updateMapMarker(id, { lat: saveLat, lng: saveLng, ...payload }) } catch(e){ console.error('Update marker failed', e) }
}

// === ПОСТАНОВКА ЗНАКОВ: прилипание к линиям ===
const SNAP_RADIUS_PX = 18   // на каком расстоянии от линии знак к ней прилипает
const SNAP_END_PX = 12      // у конца линии — встаёт точно в торец
const SIDE_GAP_PX = 4       // зазор между линией и знаком при постановке сбоку

// Точечный знак (иконка/элемент), а не инструмент рисования
function isPlaceableKey(key) {
  return typeof key === 'string' && !!key && !key.startsWith('draw_') &&
    !/^(line|polygon|circle|rect|text|el):/.test(key)
}

// Все линии и границы областей на карте в экранных координатах.
function snapTargets(excludeId) {
  const map = mapRef.value
  const out = []
  const addLayer = (layer) => {
    if (layer instanceof L.Circle) {
      if (layer._radius) out.push({ circle: true, c: map.latLngToContainerPoint(layer.getLatLng()), r: layer._radius })
      return
    }
    if (!(layer instanceof L.Polyline)) return
    const raw = layer.getLatLngs()
    const rings = Array.isArray(raw[0]) ? raw : [raw]
    const closed = layer instanceof L.Polygon
    rings.forEach(r => {
      if (r.length >= 2) out.push({ pts: r.map(p => map.latLngToContainerPoint(p)), closed })
    })
  }
  markersById.forEach((m, id) => {
    if (id === excludeId || !m) return
    if (m instanceof L.LayerGroup) m.eachLayer(addLayer)
    else addLayer(m)
  })
  return out
}

const deg = (rad) => rad * 180 / Math.PI

// → { point: L.Point (экран), angle: градусы вдоль линии, end: bool } | null
function snapToLines(latlng, excludeId) {
  const map = mapRef.value
  if (!map) return null
  const P = map.latLngToContainerPoint(latlng)
  let best = null
  const consider = (d2, point, angle, end = false) => {
    if (!best || d2 < best.d2) best = { d2, point, angle, end }
  }
  for (const t of snapTargets(excludeId)) {
    if (t.circle) {
      const dx = P.x - t.c.x, dy = P.y - t.c.y
      const len = Math.hypot(dx, dy) || 1
      const point = L.point(t.c.x + dx / len * t.r, t.c.y + dy / len * t.r)
      // касательная к окружности (по часовой стрелке на экране)
      consider((len - t.r) ** 2, point, deg(Math.atan2(dx, -dy)))
      continue
    }
    const { pts, closed } = t
    const n = pts.length
    if (!closed) {
      // Концы линии имеют приоритет: элемент встаёт в торец, «наружу».
      const first = pts[0], last = pts[n - 1]
      const dFirst = P.distanceTo(first), dLast = P.distanceTo(last)
      if (dFirst <= SNAP_END_PX) consider(-1 + dFirst / 1000, first, deg(Math.atan2(first.y - pts[1].y, first.x - pts[1].x)), true)
      if (dLast <= SNAP_END_PX) consider(-1 + dLast / 1000, last, deg(Math.atan2(last.y - pts[n - 2].y, last.x - pts[n - 2].x)), true)
    }
    const segs = closed ? n : n - 1
    for (let i = 0; i < segs; i++) {
      const A = pts[i], B = pts[(i + 1) % n]
      const vx = B.x - A.x, vy = B.y - A.y
      const lenSq = vx * vx + vy * vy
      if (!lenSq) continue
      const k = Math.max(0, Math.min(1, ((P.x - A.x) * vx + (P.y - A.y) * vy) / lenSq))
      const Q = L.point(A.x + k * vx, A.y + k * vy)
      consider((P.x - Q.x) ** 2 + (P.y - Q.y) ** 2, Q, deg(Math.atan2(vy, vx)))
    }
  }
  if (!best || best.d2 > SNAP_RADIUS_PX * SNAP_RADIUS_PX) return null
  return best
}

// Где и под каким углом ставить знак key рядом с курсором.
// → { latlng, rot, snapped }
function computePlacement(latlng, key, baseRot = 0, sy = 1, excludeId = null) {
  const free = { latlng, rot: baseRot, snapped: false }
  if (!props.snapToLine) return free
  const s = snapToLines(latlng, excludeId)
  if (!s) return free
  const map = mapRef.value
  let pt = s.point
  const side = s.end ? 'on' : props.elementSide
  if (side === 'left' || side === 'right') {
    const { h } = elementGeometry({ key, sx: 1, sy, rot: 0 })
    const off = h / 2 + SIDE_GAP_PX
    const a = s.angle * Math.PI / 180
    const sign = side === 'left' ? 1 : -1
    // «слева» по ходу линии: нормаль (sin a, −cos a) в экранных координатах (y вниз)
    pt = L.point(pt.x + sign * Math.sin(a) * off, pt.y - sign * Math.cos(a) * off)
  }
  // Файловые иконки (застава, наряд…) не поворачиваем — они всегда «прямо».
  const rot = isLibKey(key) ? s.angle : baseRot
  return { latlng: map.containerPointToLatLng(pt), rot, snapped: true }
}

// Полупрозрачный «призрак» знака под курсором — видно, куда и как он встанет.
let ghostMarker = null
// Во время анимации зума призрак не трогаем: удаление маркера посреди
// zoomanim роняет Leaflet (_animateZoom на маркере без карты).
let isZooming = false

function removeGhost() {
  if (ghostMarker) { ghostMarker.remove(); ghostMarker = null }
}

function onGhostZoomStart() {
  isZooming = true
  removeGhost()
}

function onGhostZoomEnd() {
  isZooming = false
}

function onGhostMouseOut() {
  if (!isZooming) removeGhost()
}

// Призрак не должен ловить мышь: иначе уход курсора с его DOM даёт карте
// mouseout (и клик по нему не доходит до карты).
function markGhostElement() {
  const node = ghostMarker?.getElement()
  if (node) node.classList.add('map-marker--ghost')
}

function updateGhost(latlng) {
  const tool = props.selectedIcon
  // toRaw: через реактивный прокси карты Leaflet не находит подписку маркера
  // на zoomanim при удалении, и удалённый призрак падает на следующем зуме.
  const map = toRaw(mapRef.value)
  if (isZooming) return
  if (props.readOnly || !map || !isPlaceableKey(tool)) { removeGhost(); return }
  const place = computePlacement(latlng, tool)
  const img = encodeEl({ key: tool, color: props.selectedColor, sx: 1, sy: 1, rot: place.rot })
  if (!ghostMarker) {
    ghostMarker = L.marker(place.latlng, {
      icon: buildIcon(img, false), interactive: false, keyboard: false, opacity: 0.55, zIndexOffset: 2000,
    }).addTo(map)
    ghostMarker.options.imgName = img
    markGhostElement()
  } else if (ghostMarker.options.imgName !== img) {
    ghostMarker.setIcon(buildIcon(img, false))
    ghostMarker.options.imgName = img
    markGhostElement()
  }
  ghostMarker.setLatLng(place.latlng)
}

async function placeElementAt(latlng) {
  const tool = props.selectedIcon
  const place = computePlacement(latlng, tool)
  const img_name = encodeEl({ key: tool, color: props.selectedColor, sx: 1, sy: 1, rot: place.rot })
  const lat = Number((place.latlng.lat * (scaleY || 1)).toFixed(6))
  const lng = Number((place.latlng.lng * (scaleX || 1)).toFixed(6))
  const payload = { lat, lng, img_name }
  try {
    const created = await createMapMarker(payload)
    drawExistingMarker(created)
    pushHistory({ type: 'create', id: created?.id, payload, latlng: place.latlng })
  } catch (e) { console.error('Create marker failed', e) }
}

// === ПАНЕЛЬ ВЫДЕЛЕННОГО ЗНАКА (внизу карты): размер, поворот, цвет ===
const selectedEl = ref(null)

function elStateFor(id, marker) {
  const imgName = marker?.options?.imgName
  if (!marker?.setIcon || typeof imgName !== 'string') return null
  const el = parseEl(imgName) || (isPlaceableKey(imgName) ? { key: imgName, color: null, sx: 1, sy: 1, rot: 0 } : null)
  if (!el) return null
  const lib = isLibKey(el.key) ? getLineElement(el.key) : null
  return { id, ...el, isLib: !!lib, label: lib ? lib.label : 'Знак' }
}

const elSaveTimers = new Map()
function scheduleElSave(id, img_name) {
  clearTimeout(elSaveTimers.get(id))
  elSaveTimers.set(id, setTimeout(async () => {
    elSaveTimers.delete(id)
    try { await updateMapMarker(id, { img_name }) } catch (e) { console.error('Update element failed', e) }
  }, 350))
}

// === Растяжение знака ручками (вверх / вниз / влево / вправо) ===
// Тянем ручку — противоположный край знака стоит на месте. Работает в осях
// самого знака, поэтому и для повёрнутых знаков. Слушаем mousedown в фазе
// захвата на контейнере карты, чтобы Leaflet не начал перетаскивать маркер.
let resizeState = null
let suppressMapClickUntil = 0

function onHandleMouseDown(e) {
  const handle = e.target?.closest?.('[data-handle]')
  if (!handle || props.readOnly) return
  const s = selectedEl.value
  const marker = s && markersById.get(s.id)
  const map = mapRef.value
  if (!marker || !map) return
  e.preventDefault()
  e.stopPropagation()
  const a = s.rot * Math.PI / 180
  resizeState = {
    dir: handle.dataset.handle,
    s0: { ...s },
    g0: elementGeometry(s),
    A0: map.latLngToContainerPoint(marker.getLatLng()),
    start: { x: e.clientX, y: e.clientY },
    u: { x: Math.cos(a), y: Math.sin(a) },    // ось ширины знака на экране
    v: { x: -Math.sin(a), y: Math.cos(a) },   // ось высоты (вниз)
    marker,
    latlng: null,
  }
  map.dragging.disable()
  marker.dragging?.disable()
  window.addEventListener('mousemove', onHandleMouseMove)
  window.addEventListener('mouseup', onHandleMouseUp)
}

function onHandleMouseMove(e) {
  const r = resizeState
  const map = mapRef.value
  if (!r || !map) return
  const horiz = r.dir === 'e' || r.dir === 'w'
  const axis = horiz ? r.u : r.v
  const key = horiz ? 'sx' : 'sy'
  const size0 = horiz ? r.g0.w : r.g0.h
  const anchor0 = horiz ? r.g0.ax : r.g0.ay   // от левого/верхнего края до точки привязки
  const along = (e.clientX - r.start.x) * axis.x + (e.clientY - r.start.y) * axis.y
  const grow = (r.dir === 'e' || r.dir === 's') ? along : -along
  const scale = clampScale(r.s0[key] * Math.max(size0 + grow, 4) / size0)
  const size = size0 * scale / r.s0[key]
  const frac = anchor0 / size0
  // Неподвижный край: левый/верхний при e/s, правый/нижний при w/n.
  const fixed = (r.dir === 'e' || r.dir === 's') ? -anchor0 : size0 - anchor0
  const offset = (r.dir === 'e' || r.dir === 's') ? fixed + frac * size : fixed - (1 - frac) * size
  const pt = L.point(r.A0.x + offset * axis.x, r.A0.y + offset * axis.y)
  r.latlng = map.containerPointToLatLng(pt)
  r.marker.setLatLng(r.latlng)
  applyElChange({ [key]: scale })
}

async function onHandleMouseUp() {
  const r = resizeState
  resizeState = null
  window.removeEventListener('mousemove', onHandleMouseMove)
  window.removeEventListener('mouseup', onHandleMouseUp)
  mapRef.value?.dragging.enable()
  if (!r) return
  if (!props.readOnly) r.marker.dragging?.enable()
  // mouseup после растяжения даёт карте click — не снимаем им выделение
  suppressMapClickUntil = Date.now() + 300
  if (!r.latlng) return
  const lat = Number((r.latlng.lat * (scaleY || 1)).toFixed(6))
  const lng = Number((r.latlng.lng * (scaleX || 1)).toFixed(6))
  try { await updateMapMarker(r.s0.id, { lat, lng }) } catch (e) { console.error('Update marker failed', e) }
}

const EL_AXES = [
  { key: 'sx', label: 'Ширина' },
  { key: 'sy', label: 'Высота' },
]

// Равномерно увеличить/уменьшить, сохраняя растяжение (упираемся в пределы).
function resizeElUniform(factor) {
  const s = selectedEl.value
  if (!s) return
  const maxF = EL_SCALE_MAX / Math.max(s.sx, s.sy)
  const minF = EL_SCALE_MIN / Math.min(s.sx, s.sy)
  const f = Math.min(Math.max(factor, minF), maxF)
  applyElChange({ sx: s.sx * f, sy: s.sy * f })
}

function applyElChange(patch) {
  const s = selectedEl.value
  const marker = s && markersById.get(s.id)
  if (!marker) return
  const next = { ...s, ...patch }
  next.sx = clampScale(next.sx)
  next.sy = clampScale(next.sy)
  next.rot = normRot(next.rot)
  selectedEl.value = next
  const img = encodeEl(next)
  marker.options.imgName = img
  updateMarkerIcon(marker, img, true)
  scheduleElSave(s.id, img)
}

// === DRAWING LOGIC ===
// Variables moved to Vertex Based section below to avoid duplicates



function startInlineTextEdit(latlng) {
  if (!mapRef.value) return
  
  // Удаляем предыдущий, если был
  if (tempTextMarker) {
    markerLayer.value.removeLayer(tempTextMarker)
    tempTextMarker = null
  }

  const inputId = 'map-inline-input-' + Date.now()
  const color = props.selectedColor || '#DC2626'
  const icon = L.divIcon({
    className: 'map-text-input',
    html: `<input id="${inputId}" type="text" style="border: 1px solid ${color}; color: ${color}; font-weight: bold; background: rgba(255,255,255,0.8); padding: 2px 4px; font-size: 14px; min-width: 150px; outline: none;" placeholder="Введите текст..." />`,

    iconSize: [0, 0],
    iconAnchor: [0, 8] // Смещение, чтобы инпут не перекрывал точку клика
  })

  tempTextMarker = L.marker(latlng, { icon, draggable: false }).addTo(markerLayer.value)

  // Фокус на инпут после рендера
  setTimeout(() => {
    const el = document.getElementById(inputId)
    if (el) {
      el.focus()
      
      const finalize = async () => {
        const text = el.value.trim()
        // Удаляем временный маркер
        if (tempTextMarker) {
          markerLayer.value.removeLayer(tempTextMarker)
          tempTextMarker = null
        }
        
        if (text) {
           await createAndAddMarker(latlng, `text:${color}:${text}`)
        }

      }

      el.addEventListener('blur', finalize)
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          el.blur() // Вызовет finalize
        }
      })
    }
  }, 100)
}

async function onMapClick(evt){
  if (props.readOnly) return  // ← Добавить эту строку
  if(props.deleteMode) return
  if(!props.selectedIcon) return

  if (props.selectedIcon === 'draw_eraser') return

  if (props.selectedIcon === 'draw_text') {
      startInlineTextEdit(evt.latlng)
      return
  }

  if (isPlaceableKey(props.selectedIcon)) await placeElementAt(evt.latlng)
}

function isFreehandLineTool(tool) {
  // Все шаблоны линий из markupTools рисуются свободно, как в Paint.
  return !!LINE_PRESETS[tool]
}

function onMapClickRouter(e) {
    if (props.readOnly) return
    const tool = props.selectedIcon
    // Клик по пустому месту карты без инструмента — снимаем выделение знака.
    // (Клик по линии/области тоже всплывает сюда — его не считаем.)
    if (Date.now() < suppressMapClickUntil) return
    if (!tool) {
        const target = e.originalEvent?.target
        if (!target?.classList?.contains('leaflet-interactive')) setSelectedMarker(null)
        return
    }
    // Все шаблоны линий и области — реагируют на mousedown, не на click
    if (isFreehandLineTool(tool) || isShapeTool(tool) || tool === 'draw_polygon_free') {
        return
    }
    if (tool === 'draw_polygon') {
        onMapClickPolygon(e)
        return
    }
    // По ТЗ 03.09.2026 (баг: «точки над гибкими знаками»): старая click-based
    // ветка `onMapClickDrawing` для неизвестных draw_* инструментов создавала
    // синие vertex-точки поверх реальных freehand-линий. Все известные draw_*
    // уже покрыты LINE_PRESETS / polygon / circle / rectangle / text / eraser,
    // поэтому ветку убрали. Всё непокрытое падает в общий onMapClick (постановка
    // маркера-иконки), где vertex-точки не рождаются.
    onMapClick(e)
}

// ============ РИСОВАНИЕ ЛИНИЙ КУРСОРОМ (по всем шаблонам markupTools) ============
// По ТЗ (встреча 27.08.2026, п.5): линии рисуются курсором мыши как в Windows Paint.
// Стиль (dashArray, цвет, толщина, парная линия, стрелка, штрих) задаётся LINE_PRESETS.
let freehandActive = false
let freehandPolyline = null    // основная (внешняя) линия
let freehandParallel = null    // вторая линия для double-режимов
let freehandPoints = []
let freehandPreset = null      // текущий пресет стиля

function startFreehand(latlng) {
  const map = mapRef.value
  if (!map) return
  freehandActive = true
  freehandPoints = [latlng]

  const { style, preset } = lineStyleFor(props.selectedIcon)
  freehandPreset = preset

  // По ТЗ 03.09.2026: КСП — layerGroup из двух параллельных линий + штрихов
  // между ними. Пересобираем группу в extendFreehand при каждом новом
  // сегменте (быстрее и надёжнее, чем гонять pattern через SVG defs).
  if (preset.type === 'ksp') {
    freehandPolyline = L.polyline([latlng], {
      color: style.color, weight: Math.max(1, (style.weight || 2)),
      opacity: 0, interactive: false,   // невидимая ось-приёмник событий
    }).addTo(map)
    freehandKspGroup = L.layerGroup().addTo(map)
    map.dragging.disable(); map.doubleClickZoom.disable()
    return
  }

  freehandPolyline = L.polyline([latlng], {
    ...style,
    opacity: 0.95,
    interactive: false,
  }).addTo(map)

  // По ТЗ 03.09.2026: для двойных инструментов (draw_double / draw_double_dashed
  // / draw_arrow_double) freehandPolyline становится «левой» дорожкой,
  // freehandParallel — «правой».
  if (preset.double) {
    freehandParallel = L.polyline([latlng], {
      ...style,
      interactive: false,
    }).addTo(map)
  }

  map.dragging.disable()
  map.doubleClickZoom.disable()
}

let freehandKspGroup = null

function extendFreehand(latlng) {
  if (!freehandActive || !freehandPolyline) return
  freehandPoints.push(latlng)
  const map = mapRef.value
  // KSP: перестраиваем layer group (две линии + штрихи) при каждом сегменте
  if (freehandPreset && freehandPreset.type === 'ksp' && freehandKspGroup && freehandPoints.length >= 2) {
    freehandKspGroup.clearLayers()
    const weight = (freehandPolyline.options.weight) || 2
    const grp = buildKspLayer(map, freehandPoints, freehandPolyline.options.color, weight)
    grp.eachLayer(l => freehandKspGroup.addLayer(l))
    return
  }
  if (freehandParallel) {
    const { left, right } = parallelOffsetPoints(map, freehandPoints, DOUBLE_OFFSET_PX)
    freehandPolyline.setLatLngs(left)
    freehandParallel.setLatLngs(right)
  } else {
    freehandPolyline.addLatLng(latlng)
  }
}

function finishFreehand() {
  const map = mapRef.value
  if (!freehandActive) return
  freehandActive = false
  map?.dragging.enable()
  map?.doubleClickZoom.enable()
  if (freehandPoints.length < 2) {
    if (freehandPolyline) freehandPolyline.remove()
    if (freehandParallel) freehandParallel.remove()
    if (freehandKspGroup) freehandKspGroup.remove()
  }
  // Live-элементы удаляются после сохранения — drawExistingMarker
  // отрисует финальный вариант поверх, а живые превью нужно снять.
  if (freehandPolyline) freehandPolyline.remove()
  if (freehandParallel) freehandParallel.remove()
  if (freehandKspGroup) freehandKspGroup.remove()
  freehandPolyline = null
  freehandParallel = null
  freehandKspGroup = null
  freehandPoints = []
}

// ============ ФИГУРЫ (круг, прямоугольник) ============
let shapeStartLatLng = null
let shapePreview = null

function startShape(latlng) {
  const map = mapRef.value
  if (!map) return
  shapeStartLatLng = latlng
  map.dragging.disable()
  const style = { ...areaStyle(currentAreaStyle()), className: '', interactive: false }
  if (props.selectedIcon === 'draw_circle') {
    shapePreview = L.circle(latlng, { ...style, radius: 1 }).addTo(map)
  } else if (props.selectedIcon === 'draw_rectangle') {
    shapePreview = L.rectangle(L.latLngBounds(latlng, latlng), style).addTo(map)
  }
}

function updateShape(latlng) {
  if (!shapeStartLatLng || !shapePreview) return
  if (props.selectedIcon === 'draw_circle') {
    const r = shapeStartLatLng.distanceTo(latlng)
    shapePreview.setRadius(r)
  } else if (props.selectedIcon === 'draw_rectangle') {
    shapePreview.setBounds(L.latLngBounds(shapeStartLatLng, latlng))
  }
}

function finishShape() {
  const map = mapRef.value
  map?.dragging.enable()
  shapeStartLatLng = null
  shapePreview = null
}

// ============ ОБЛАСТЬ ОТ РУКИ (лассо) ============
// По встрече 03.09.2026: «круги и произвольные формы». Зажал ЛКМ, обвёл контур,
// отпустил — контур замыкается и сохраняется обычным polygon:… (как клик-за-кликом).
let lassoPoints = []
let lassoPreview = null

function startLasso(latlng) {
  const map = mapRef.value
  if (!map) return
  lassoPoints = [latlng]
  const opts = currentAreaStyle()
  lassoPreview = L.polygon([latlng], { ...areaStyle(opts), className: '', interactive: false }).addTo(map)
  map.dragging.disable()
  map.doubleClickZoom.disable()
}

function extendLasso(latlng) {
  if (!lassoPreview) return
  lassoPoints.push(latlng)
  lassoPreview.setLatLngs(lassoPoints)
}

async function finishLasso() {
  const map = mapRef.value
  let pts = lassoPoints
  if (lassoPreview) lassoPreview.remove()
  lassoPreview = null
  lassoPoints = []
  map?.dragging.enable()
  map?.doubleClickZoom.enable()
  if (!map || pts.length < 3) return
  // Как у карандаша: убираем субпиксельные точки, иначе контур из сотен вершин.
  try {
    const simplified = L.LineUtil.simplify(pts.map(p => map.latLngToLayerPoint(p)), 2)
    if (simplified.length >= 3) pts = simplified.map(lp => map.layerPointToLatLng(lp))
  } catch (_) { /* оставляем исходные */ }
  if (pts.length < 3) return
  const savedPts = pts.map(p => ({
    lat: Number((p.lat * (scaleY || 1)).toFixed(6)),
    lng: Number((p.lng * (scaleX || 1)).toFixed(6)),
  }))
  const img_name = encodeArea('polygon', currentAreaStyle(), savedPts.map(p => `${p.lat},${p.lng}`).join(';'))
  try {
    const payload = { lat: savedPts[0].lat, lng: savedPts[0].lng, img_name }
    const created = await createMapMarker(payload)
    drawExistingMarker(created)
    pushHistory({ type: 'create', id: created?.id, payload, latlng: { lat: pts[0].lat, lng: pts[0].lng } })
  } catch (err) { console.error('lasso save failed', err) }
}

// ============ ROUTER: mousedown / move / up ============
function isShapeTool(t) { return t === 'draw_circle' || t === 'draw_rectangle' }

// === Ластик Paint-like (по ТЗ 03.09.2026: карта = холст с рисованием и стиранием) ===
// mousedown → активируется режим стирания, mousemove → удаляем всё что попадает
// в круг радиуса ERASER_RADIUS_PX под курсором, mouseup → выключаем.
const ERASER_RADIUS_PX = 24
let eraserActive = false
let eraserErased = new Set()  // защита от повторного удаления одного и того же id за одну сессию

function distSqLatLngPx(map, latA, latB) {
  const a = map.latLngToContainerPoint(latA)
  const b = map.latLngToContainerPoint(latB)
  const dx = a.x - b.x, dy = a.y - b.y
  return dx*dx + dy*dy
}

// Развернуть возможно вложенные массивы latlngs (полигон / мульти-линия).
function flattenLatLngs(latlngs, out) {
  if (!latlngs) return
  if (Array.isArray(latlngs)) {
    for (const item of latlngs) flattenLatLngs(item, out)
  } else if (typeof latlngs.lat === 'number' && typeof latlngs.lng === 'number') {
    out.push(latlngs)
  }
}

async function eraseAt(latlng) {
  const map = mapRef.value
  if (!map || !latlng) return
  const r2 = ERASER_RADIUS_PX * ERASER_RADIUS_PX
  let cursorPx
  try { cursorPx = map.latLngToContainerPoint(latlng) } catch { return }
  const toDelete = []
  markersById.forEach((m, id) => {
    if (eraserErased.has(id) || !m) return
    try {
      // Маркеры (divIcon) — расстояние центра до курсора
      if (m instanceof L.Marker) {
        const ll = m.getLatLng && m.getLatLng()
        if (!ll) return
        if (distSqLatLngPx(map, latlng, ll) <= r2) toDelete.push(id)
        return
      }
      // Круги — проверяем центр
      if (typeof L.Circle !== 'undefined' && m instanceof L.Circle) {
        const ll = m.getLatLng && m.getLatLng()
        if (ll && distSqLatLngPx(map, latlng, ll) <= r2) toDelete.push(id)
        return
      }
      // Полилинии/полигоны — проверяем точки и сегменты
      const linesToTest = []
      if (m instanceof L.Polyline) linesToTest.push(m)
      else if (m instanceof L.LayerGroup) {
        m.eachLayer(l => { if (l instanceof L.Polyline) linesToTest.push(l) })
      }
      for (const line of linesToTest) {
        const raw = line.getLatLngs && line.getLatLngs()
        const pts = []
        flattenLatLngs(raw, pts)
        for (const p of pts) {
          if (!p) continue
          if (distSqLatLngPx(map, latlng, p) <= r2) { toDelete.push(id); return }
        }
        for (let i = 1; i < pts.length; i++) {
          const p1 = pts[i-1], p2 = pts[i]
          if (!p1 || !p2) continue
          const A = map.latLngToContainerPoint(p1)
          const B = map.latLngToContainerPoint(p2)
          const d2 = pointToSegDist2(cursorPx.x, cursorPx.y, A.x, A.y, B.x, B.y)
          if (d2 <= r2) { toDelete.push(id); return }
        }
      }
    } catch (_) { /* skip broken layer */ }
  })
  for (const id of toDelete) {
    eraserErased.add(id)
    try { await deleteMarkerById(id) } catch (_) {}
  }
}

// Кратчайшее расстояние² от точки P до отрезка AB
function pointToSegDist2(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay
  const lenSq = dx*dx + dy*dy
  if (lenSq === 0) { const ex = px - ax, ey = py - ay; return ex*ex + ey*ey }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const qx = ax + t * dx, qy = ay + t * dy
  const ex = px - qx, ey = py - qy
  return ex*ex + ey*ey
}

function onMapMouseDown(e) {
  if (props.readOnly) return
  const tool = props.selectedIcon
  if (tool === 'draw_eraser') {
    L.DomEvent.stop(e.originalEvent)
    eraserActive = true
    eraserErased = new Set()
    mapRef.value?.dragging.disable()
    eraseAt(e.latlng)
    return
  }
  if (isFreehandLineTool(tool)) {
    L.DomEvent.stop(e.originalEvent)
    startFreehand(e.latlng)
  } else if (isShapeTool(tool)) {
    L.DomEvent.stop(e.originalEvent)
    startShape(e.latlng)
  } else if (tool === 'draw_polygon_free') {
    L.DomEvent.stop(e.originalEvent)
    startLasso(e.latlng)
  }
}
function onMapMouseMove(e) {
  if (eraserActive) { eraseAt(e.latlng); return }
  if (lassoPreview) { extendLasso(e.latlng); return }
  if (isPlaceableKey(props.selectedIcon)) { updateGhost(e.latlng); return }
  if (freehandActive) extendFreehand(e.latlng)
  else if (shapeStartLatLng) updateShape(e.latlng)
}
async function onMapMouseUp() {
  if (eraserActive) {
    eraserActive = false
    eraserErased = new Set()
    mapRef.value?.dragging.enable()
    return
  }
  if (lassoPreview) {
    await finishLasso()
    return
  }
  if (freehandActive) {
    // Сохраняем линию с типом соответствующим пресету (freehand/solid/dashed/…)
    let pts = freehandPoints.slice()
    const preset = freehandPreset || { type: 'solid' }
    const color = preset.colorOverride || (props.selectedColor || '#DC2626')
    const weight = (props.strokeWidth || 3) + (preset.weightAdd || 0)
    const map = mapRef.value
    finishFreehand()
    if (pts.length >= 2) {
      // По ТЗ 03.09.2026 («линии становятся жирнее после отрисовки»): freehand
      // при mousemove копил десятки-сотни точек с шагом < 1 px. При dashArray
      // с lineCap='round' каждый лишний излом добавлял «круг» → линия
      // визуально плотнее чем во время рисования. Прогоняем L.LineUtil.simplify
      // с толерантностью 2 px — форма сохраняется, точек в 5-10× меньше,
      // dashArray работает как в live-режиме.
      if (map && L.LineUtil && L.LineUtil.simplify) {
        try {
          const layerPts = pts.map(p => map.latLngToLayerPoint(p))
          const simplified = L.LineUtil.simplify(layerPts, 2)
          if (simplified && simplified.length >= 2) {
            pts = simplified.map(lp => map.layerPointToLatLng(lp))
          }
        } catch (_) { /* fallback: оставляем исходные */ }
      }
      try {
        const scaled = pts.map(p => ({
          lat: Number((p.lat * (scaleY || 1)).toFixed(6)),
          lng: Number((p.lng * (scaleX || 1)).toFixed(6)),
        }))
        const img_name = encodeLine(color, preset.type, scaled, weight)
        const first = pts[0]
        const saveLat = Number((first.lat * (scaleY || 1)).toFixed(6))
        const saveLng = Number((first.lng * (scaleX || 1)).toFixed(6))
        const payload = { lat: saveLat, lng: saveLng, img_name }
        const created = await createMapMarker(payload)
        // ВАЖНО: перерисовываем через drawExistingMarker, чтобы линия получила
        // markerId и click-handler → ластик по ней сработает.
        drawExistingMarker(created)
        pushHistory({ type: 'create', id: created?.id, payload, latlng: { lat: first.lat, lng: first.lng } })
      } catch (err) { console.error('line save failed', err) }
    }
  } else if (shapeStartLatLng && shapePreview) {
    const tool = props.selectedIcon
    const startLL = shapeStartLatLng
    const shape = shapePreview
    const areaOpts = currentAreaStyle()
    finishShape()
    // Превью снимаем: сохранённая фигура отрисуется через drawExistingMarker.
    shape.remove()
    try {
      let img_name = null
      let anchor = startLL
      if (tool === 'draw_circle') {
        const centerLL = shape.getLatLng ? shape.getLatLng() : startLL
        const radius = shape.getRadius ? shape.getRadius() : 0
        if (radius < 1) return
        const cLat = Number((centerLL.lat * (scaleY || 1)).toFixed(6))
        const cLng = Number((centerLL.lng * (scaleX || 1)).toFixed(6))
        img_name = encodeArea('circle', areaOpts, `${cLat},${cLng}:${Number(radius.toFixed(3))}`)
        anchor = centerLL
      } else if (tool === 'draw_rectangle') {
        const b = shape.getBounds()
        const sw = b.getSouthWest(); const ne = b.getNorthEast()
        const swLat = Number((sw.lat * (scaleY || 1)).toFixed(6))
        const swLng = Number((sw.lng * (scaleX || 1)).toFixed(6))
        const neLat = Number((ne.lat * (scaleY || 1)).toFixed(6))
        const neLng = Number((ne.lng * (scaleX || 1)).toFixed(6))
        img_name = encodeArea('rect', areaOpts, `${swLat},${swLng}:${neLat},${neLng}`)
        anchor = sw
      }
      if (img_name) {
        const saveLat = Number((anchor.lat * (scaleY || 1)).toFixed(6))
        const saveLng = Number((anchor.lng * (scaleX || 1)).toFixed(6))
        const payload = { lat: saveLat, lng: saveLng, img_name }
        const created = await createMapMarker(payload)
        drawExistingMarker(created)
        pushHistory({ type: 'create', id: created?.id, payload, latlng: { lat: anchor.lat, lng: anchor.lng } })
      }
    } catch (err) { console.error('shape save failed', err) }
  }
}

async function createAndAddMarker(latlng, img_name) {
    try {
        const lat = Number(latlng.lat.toFixed(6))
        const lng = Number(latlng.lng.toFixed(6))

        const saveLat = Number((lat * (scaleY || 1)).toFixed(6))
        const saveLng = Number((lng * (scaleX || 1)).toFixed(6))

        // По фидбеку 2026-09-02: prompt подписи при постановке убран (мешает).
        // Подпись редактируется правым кликом по знаку (см. onMarkerContext).
        const payload = { lat: saveLat, lng: saveLng, img_name }

        const created = await createMapMarker(payload)
        addMarkerAt({ lat, lng }, img_name, created?.id, '')
        pushHistory({ type: 'create', id: created?.id, payload, latlng: { lat, lng } })
    } catch(e) { console.error('Create marker failed', e) }
}

// === UNDO / REDO для редактора карты ===
// По ТЗ (встреча 27.08.2026): «Paint-like инструменты». Ведём два стека —
// undoStack и redoStack. Стек ограничен 50 действиями, чтобы не раздувать память.
const HISTORY_MAX = 50
const undoStack = []
const redoStack = []
function pushHistory(action) {
  undoStack.push(action)
  if (undoStack.length > HISTORY_MAX) undoStack.shift()
  redoStack.length = 0 // новое действие обнуляет ветку «повторить»
}

async function undoLast() {
  const action = undoStack.pop()
  if (!action) return false
  try {
    if (action.type === 'create' && action.id != null) {
      await deleteMapMarker(action.id)
      const layer = markersById.get(action.id)
      if (layer) { try { markerLayer.value?.removeLayer(layer) } catch {} markersById.delete(action.id) }
      if (selectedMarkerId.value === action.id) setSelectedMarker(null)
      redoStack.push(action)
    }
  } catch (err) { console.error('undo failed', err) }
  return true
}

async function redoLast() {
  const action = redoStack.pop()
  if (!action) return false
  try {
    if (action.type === 'create') {
      const created = await createMapMarker(action.payload)
      // drawExistingMarker умеет все типы (линии, области, элементы) —
      // addMarkerAt раньше превращал повторённую линию в иконку.
      drawExistingMarker(created)
      // Пересохраняем id (мог измениться)
      undoStack.push({ ...action, id: created?.id })
    }
  } catch (err) { console.error('redo failed', err) }
  return true
}

function historyState() {
  return { canUndo: undoStack.length > 0, canRedo: redoStack.length > 0 }
}

function onLayerClick(e) {
  if (props.selectedIcon === 'draw_eraser') {
      // Check if clicked target has markerId directly
      let id = e.target?.options?.markerId
      
      if (!id) {
          if (!id) {
              // Leaflet doesn't automatically link layers to their parent group
              // But we stored the group in markersById. 
              // However, here we only have the layer.
              // Let's rely on the layer having markerId assigned in drawExisting
          }
      }

      if (id) {
          emit('request-delete-marker', id)
      } else {
        // Fallback for unsaved items?
        markerLayer.value.removeLayer(e.target)
      }

  }

}

let currentPolyline = null
let currentInnerPoly = null
let isDrawing = false
let tempTextMarker = null
let lineVertexMarkers = []
let linePoints = []

let currentPolygon = null
let polygonPoints = []
let isDrawingPolygon = false
let polygonVertexMarkers = []
let isDraggingVertex = false

function createVertexMarker(latlng, index) {
  const marker = L.circleMarker(latlng, {
    radius: 6,
    color: '#2563EB',
    fillColor: '#fff',
    fillOpacity: 1,
    weight: 2,
    draggable: false
  }).addTo(markerLayer.value)
  
  marker._vertexIndex = index
  
  marker.on('mousedown', (e) => {
    L.DomEvent.stopPropagation(e)
    startVertexDrag(marker, e)
  })
  
  return marker
}

function startVertexDrag(marker, e) {
  const map = mapRef.value
  if (!map) return
  
  map.dragging.disable()
  
  const onMove = (moveEvent) => {
    marker.setLatLng(moveEvent.latlng)
    linePoints[marker._vertexIndex] = moveEvent.latlng
    updatePolylineFromPoints()
    updateIntermediateMarkers()
  }
  
  const onUp = () => {
    map.off('mousemove', onMove)
    map.off('mouseup', onUp)
    map.dragging.enable()
  }
  
  map.on('mousemove', onMove)
  map.on('mouseup', onUp)
}

function updatePolylineFromPoints() {
  if (currentPolyline) {
    currentPolyline.setLatLngs(linePoints)
  }
  if (currentInnerPoly) {
    currentInnerPoly.setLatLngs(linePoints)
  }
}

function onMapClickDrawing(e) {
  if (!props.selectedIcon || !props.selectedIcon.startsWith('draw_') || props.selectedIcon === 'draw_text' || props.selectedIcon === 'draw_eraser' || props.selectedIcon === 'draw_polygon') return
  L.DomEvent.stopPropagation(e)

  const isDashed = props.selectedIcon === 'draw_dashed'
  const isDouble = props.selectedIcon === 'draw_double'
  const color = props.selectedColor || '#DC2626'
  
  linePoints.push(e.latlng)
  const vertexMarker = createVertexMarker(e.latlng, linePoints.length - 1)
  lineVertexMarkers.push(vertexMarker)
  
  if (!currentPolyline) {
      const options = { color: color, weight: isDouble ? 6 : 3 }
      if (isDashed) options.dashArray = '10, 10'
      
      currentPolyline = L.polyline(linePoints, options).addTo(markerLayer.value)
      isDrawing = true
      
      currentPolyline.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev)
          onMapClickDrawing(ev) 
      })

      if (isDouble) {
          currentInnerPoly = L.polyline(linePoints, { color: '#FFFFFF', weight: 2, pane: 'doubleLineInner' }).addTo(markerLayer.value)
      }
  } else {
      currentPolyline.setLatLngs(linePoints)
      if (currentInnerPoly) {
          currentInnerPoly.setLatLngs(linePoints)
      }
  }
  updateIntermediateMarkers()
}

function onMapClickPolygon(e) {
  if (isDraggingVertex) return
  L.DomEvent.stopPropagation(e)
  
  polygonPoints.push(e.latlng)
  const vertexMarker = createPolygonVertexMarker(e.latlng, polygonPoints.length - 1)
  polygonVertexMarkers.push(vertexMarker)
  
  if (!currentPolygon) {
    const opts = currentAreaStyle()
    currentPolygon = L.polygon(polygonPoints, areaStyle(opts)).addTo(markerLayer.value)
    if (opts.pattern === 'hatched') applyHatchedFill(currentPolygon, opts.stroke)
    isDrawingPolygon = true
  } else {
    currentPolygon.setLatLngs(polygonPoints)
  }
  updateIntermediateMarkers()
}

function createPolygonVertexMarker(latlng, index) {
  const marker = L.circleMarker(latlng, {
    radius: 6,
    color: '#16A34A',
    fillColor: '#fff',
    fillOpacity: 1,
    weight: 2
  }).addTo(markerLayer.value)
  
  marker._vertexIndex = index
  
  marker.on('mousedown', (e) => {
    L.DomEvent.stopPropagation(e)
    startPolygonVertexDrag(marker)
  })
  
  return marker
}

function startPolygonVertexDrag(marker) {
  const map = mapRef.value
  if (!map) return
  
  isDraggingVertex = true
  map.dragging.disable()
  
  const onMove = (moveEvent) => {
    marker.setLatLng(moveEvent.latlng)
    polygonPoints[marker._vertexIndex] = moveEvent.latlng
    if (currentPolygon) currentPolygon.setLatLngs([polygonPoints])
    updateIntermediateMarkers()
  }
  
  const onUp = () => {
    map.off('mousemove', onMove)
    map.off('mouseup', onUp)
    map.dragging.enable()
    setTimeout(() => { isDraggingVertex = false }, 100)
  }
  
  map.on('mousemove', onMove)
  map.on('mouseup', onUp)
}



function onMapDblClick(e) {
   if (isDrawing && currentPolyline) {
       L.DomEvent.stopPropagation(e)
       finishDrawing()
   }
   if (isDrawingPolygon && currentPolygon) {
       L.DomEvent.stopPropagation(e)
       finishPolygon()
   }
}

function finishDrawing() {
    if (currentPolyline) {
        savePolyline(currentPolyline)
    }
    if (currentInnerPoly) {
        markerLayer.value.removeLayer(currentInnerPoly)
        currentInnerPoly = null
    }
    lineVertexMarkers.forEach(m => markerLayer.value.removeLayer(m))
    lineVertexMarkers = []
    linePoints = []
    currentPolyline = null
    isDrawing = false
    clearIntermediateMarkers()
}

function finishPolygon() {
    if (currentPolygon && polygonPoints.length >= 3) {
        savePolygon(currentPolygon)
    } else if (currentPolygon) {
        markerLayer.value.removeLayer(currentPolygon)
    }
    polygonVertexMarkers.forEach(m => markerLayer.value.removeLayer(m))
    polygonVertexMarkers = []
    currentPolygon = null
    polygonPoints = []
    isDrawingPolygon = false
    clearIntermediateMarkers()
}

// === INTERMEDIATE MARKERS LOGIC (UMap style) ===
let intermediateMarkers = []

function clearIntermediateMarkers() {
    intermediateMarkers.forEach(m => markerLayer.value.removeLayer(m))
    intermediateMarkers = []
}

function updateIntermediateMarkers() {
    clearIntermediateMarkers()
    
    // Determine active points and loop based on mode
    let points = []
    let isPolygon = false
    
    if (isDrawing && linePoints.length >= 2) {
        points = linePoints
        isPolygon = false
    } else if (isDrawingPolygon && polygonPoints.length >= 2) {
        points = polygonPoints
        isPolygon = true
    } else {
        return
    }

    for (let i = 0; i < points.length; i++) {
        // For lines, stop at length-1. For polygons, wrap around (last -> first)
        if (!isPolygon && i === points.length - 1) break
        
        const p1 = points[i]
        const p2 = points[(i + 1) % points.length]
        
        // Compute midpoint
        const lat = (p1.lat + p2.lat) / 2
        const lng = (p1.lng + p2.lng) / 2
        const mid = L.latLng(lat, lng)
        
        createIntermediateMarker(mid, i, isPolygon)
    }
}

function createIntermediateMarker(latlng, index, isPolygon) {
    const marker = L.circleMarker(latlng, {
        radius: 6,
        color: '#2563EB',
        weight: 2,
        fillColor: '#fff',
        fillOpacity: 0.5, // Semi-transparent "ghost" look
        opacity: 0.5
    }).addTo(markerLayer.value)
    
    marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        // Insert new point at index + 1
        insertVertex(index + 1, latlng, isPolygon)
    })
    
    // Support dragging ghost marker to immediately convert it
    marker.on('mousedown', (e) => {
       L.DomEvent.stopPropagation(e)
       // Promote to real vertex immediately
       insertVertex(index + 1, latlng, isPolygon, true) // true = startDragging
    })

    intermediateMarkers.push(marker)
}

function insertVertex(newIndex, latlng, isPolygon, startDraggingNow = false) {
    // 1. Insert point into data array
    if (isPolygon) {
        polygonPoints.splice(newIndex, 0, latlng)
    } else {
        linePoints.splice(newIndex, 0, latlng)
    }
    
    // 2. Rebuild visuals
    //    We need to clear existing vertex markers and rebuild them 
    //    because their indices have shifted.
    rebuildVertexMarkers(isPolygon)
    
    // 3. Update Shape
    if (isPolygon) {
        if (currentPolygon) currentPolygon.setLatLngs([polygonPoints])
    } else {
        updatePolylineFromPoints()
    }
    
    // 4. Update Intermediate Markers (recursive logic essentially)
    updateIntermediateMarkers()
    
    // 5. If requested, immediately start dragging the new vertex
    if (startDraggingNow) {
        // Find the newly created vertex marker at newIndex
        const markers = isPolygon ? polygonVertexMarkers : lineVertexMarkers
        const newMarker = markers[newIndex]
        if (newMarker) {
             // We need to manually trigger the drag logic we wrote
             if (isPolygon) startPolygonVertexDrag(newMarker)
             else startVertexDrag(newMarker, null) 
        }
    }
}

function rebuildVertexMarkers(isPolygon) {
    // Clear old
    const oldMarkers = isPolygon ? polygonVertexMarkers : lineVertexMarkers
    oldMarkers.forEach(m => markerLayer.value.removeLayer(m))
    
    const newMarkers = []
    const points = isPolygon ? polygonPoints : linePoints
    
    points.forEach((p, i) => {
        let m 
        if (isPolygon) {
            m = createPolygonVertexMarker(p, i)
        } else {
            m = createVertexMarker(p, i)
        }
        newMarkers.push(m)
    })
    
    if (isPolygon) {
        polygonVertexMarkers = newMarkers
    } else {
        lineVertexMarkers = newMarkers
    }
}



async function savePolyline(poly) {
    const pts = poly.getLatLngs()
    if (pts.length < 2) {
        markerLayer.value.removeLayer(poly)
        return
    }
    // Encode
    const color = props.selectedColor || '#DC2626'
    let type = 'solid'
    if (props.selectedIcon === 'draw_dashed') type = 'dashed'
    if (props.selectedIcon === 'draw_double') type = 'double'
    
    // Scale points
    const savedPts = pts.map(p => {
        return {
           lat: Number((p.lat * (scaleY || 1)).toFixed(6)), 
           lng: Number((p.lng * (scaleX || 1)).toFixed(6)) 
        }
    })
    
    const first = savedPts[0]
    const img_name = encodeLine(color, type, savedPts)
    
    try {
        const created = await createMapMarker({ lat: first.lat, lng: first.lng, img_name })
        if (created && created.id) {
            if (type === 'double') {
                 markerLayer.value.removeLayer(poly)
                 
                 const outer = L.polyline(pts, { color: color, weight: 6 })
                 const inner = L.polyline(pts, { color: '#FFFFFF', weight: 2, pane: 'doubleLineInner' })
                 
                 const group = L.layerGroup([outer, inner]).addTo(markerLayer.value)
                 
                 outer.options.markerId = created.id
                 inner.options.markerId = created.id

                 outer.on('click', e => onMarkerClick(e.originalEvent, outer))

                 
                 group.options = { markerId: created.id, imgName: img_name }
                 markersById.set(created.id, group)
                 
                 // Vertex-ручки отключены (баг «синие точки»).
            } else {
                poly.options.markerId = created.id
                poly.options.imgName = img_name
                markersById.set(created.id, poly)
                poly.on('click', e => onMarkerClick(e.originalEvent, e.target))
                
                // Vertex-ручки отключены (баг «синие точки»).
            }
        }
    } catch(e) { 
        console.error('Save polyline failed', e) 
        markerLayer.value.removeLayer(poly)
    }
}

async function savePolygon(polygon) {
    const pts = polygon.getLatLngs()[0]
    if (!pts || pts.length < 3) {
        markerLayer.value.removeLayer(polygon)
        return
    }
    
    const savedPts = pts.map(p => ({
       lat: Number((p.lat * (scaleY || 1)).toFixed(6)),
       lng: Number((p.lng * (scaleX || 1)).toFixed(6))
    }))

    const first = savedPts[0]
    const pointsStr = savedPts.map(p => `${p.lat},${p.lng}`).join(';')
    const img_name = encodeArea('polygon', currentAreaStyle(), pointsStr)
    
    try {
        const created = await createMapMarker({ lat: first.lat, lng: first.lng, img_name })
        if (created && created.id) {
            polygon.options.markerId = created.id
            polygon.options.imgName = img_name
            markersById.set(created.id, polygon)
            polygon.on('click', e => onMarkerClick(e.originalEvent, e.target))
            
            // Vertex-ручки отключены (баг «синие точки»).
        }
    } catch(e) { 
        console.error('Save polygon failed', e) 
        markerLayer.value.removeLayer(polygon)
    }
}



watch(() => props.selectedIcon, (newVal) => {
  const map = mapRef.value
  if (!map) return

  if (isDrawing) finishDrawing()
  if (isDrawingPolygon) finishPolygon()
  removeGhost()

  if (newVal && newVal.startsWith('draw_') && newVal !== 'draw_text') {
    if (newVal === 'draw_eraser') {
       // По ТЗ 03.09.2026: ластик Paint-like → курсор = круг размером
       // ~ERASER_RADIUS_PX (виден точный радиус стирания).
       const r = ERASER_RADIUS_PX
       const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${2*r}' height='${2*r}' viewBox='0 0 ${2*r} ${2*r}'><circle cx='${r}' cy='${r}' r='${r-2}' fill='rgba(255,255,255,0.35)' stroke='%23111' stroke-width='2'/></svg>`
       map.getContainer().style.cursor = `url("data:image/svg+xml;utf8,${svg}") ${r} ${r}, cell`
    } else {
       map.getContainer().style.cursor = 'crosshair'
    }
  } else {
    map.getContainer().style.cursor = ''
    map.dragging.enable()
  }
})

watch(() => props.selectedColor, (newColor) => {
  if (isDrawing && currentPolyline) {
     currentPolyline.setStyle({ color: newColor })
  }
})

onMounted(() => {
  attachInterval = setInterval(() => {
    if (mapRef.value) {
      clearInterval(attachInterval)
      attachInterval = null
      const map = mapRef.value
      
      map.on('click', onMapClickRouter)
      map.on('dblclick', onMapDblClick)
      map.on('contextmenu', onMapContextMenu)
      map.on('mousedown', onMapMouseDown)
      map.on('mousemove', onMapMouseMove)
      map.on('mouseup', onMapMouseUp)
      map.on('mouseout', onGhostMouseOut)
      map.on('zoomstart', onGhostZoomStart)
      map.on('zoomend', onGhostZoomEnd)
      map.getContainer().addEventListener('mousedown', onHandleMouseDown, true)

    }

  }, 100)
})

onUnmounted(() => {
  if (resizeObserver) {
    try { resizeObserver.disconnect() } catch {}
    resizeObserver = null
  }
  if (onWindowResize) {
    try { window.removeEventListener('resize', onWindowResize) } catch {}
    onWindowResize = null
  }

  if (attachInterval) {
    clearInterval(attachInterval)
    attachInterval = null
  }

  if (mapRef.value) {
    const map = mapRef.value
    map.off('click', onMapClickRouter)
    map.off('dblclick', onMapDblClick)
    map.off('contextmenu', onMapContextMenu)
    map.off('mousedown', onMapMouseDown)
    map.off('mousemove', onMapMouseMove)
    map.off('mouseup', onMapMouseUp)
    map.off('mouseout', onGhostMouseOut)
    map.off('zoomstart', onGhostZoomStart)
    map.off('zoomend', onGhostZoomEnd)
    try { map.getContainer().removeEventListener('mousedown', onHandleMouseDown, true) } catch {}
    window.removeEventListener('mousemove', onHandleMouseMove)
    window.removeEventListener('mouseup', onHandleMouseUp)
    removeGhost()
    elSaveTimers.forEach(t => clearTimeout(t))
    map.off('zoomanim', onZoomAnim)
    map.off('zoomend', updateAllMarkersSize)

    try {
      editorsByLayerId.forEach((ctx) => ctx?.cleanup?.())
      editorsByLayerId.clear()
    } catch {}
    try { markersById.clear() } catch {}
    try { markerLayer.value?.clearLayers?.() } catch {}

    // Critical: fully dispose Leaflet instance so next mount works
    try { map.remove() } catch {}
    mapRef.value = null
  }
})

function createEditorForLayer(layer, markerId, isPolygon) {
  if (props.readOnly) return
  cleanupEditor(markerId)

  // Context for this editor
  const context = {
    handles: [],
    cleanup: () => {
      context.handles.forEach(h => markerLayer.value && markerLayer.value.removeLayer(h))
      context.handles = []
    }
  }
  editorsByLayerId.set(markerId, context)

  // Rebuild function
  const rebuild = () => {
    // Clear old handles (but keep the context active)
    context.handles.forEach(h => markerLayer.value && markerLayer.value.removeLayer(h))
    context.handles = []

    // Get current points
    // Polyline returns [latlngs], Polygon returns [[latlngs]] (outer ring)
    let latlngs = layer.getLatLngs()
    if (isPolygon && Array.isArray(latlngs) && Array.isArray(latlngs[0])) {
      latlngs = latlngs[0] // Outer ring
    }
    
    // Safety check
    if (!Array.isArray(latlngs) || latlngs.length === 0) return

    // 1. Vertex Markers
    latlngs.forEach((p, i) => {
       const vMarker = L.circleMarker(p, {
         radius: 6,
         color: isPolygon ? '#16A34A' : '#2563EB',
         fillColor: '#fff',
         fillOpacity: 1,
         weight: 2,
         draggable: false // We use custom drag
       }).addTo(markerLayer.value)
       
       context.handles.push(vMarker)

       // Custom drag implementation
       vMarker.on('mousedown', (e) => {
         L.DomEvent.stopPropagation(e)
         const map = mapRef.value
         if (!map) return
         
         map.dragging.disable()
         
         const onMove = (mv) => {
            const newPos = mv.latlng
            vMarker.setLatLng(newPos)
            latlngs[i] = newPos
            
            // Update Layer visual
            if (isPolygon) layer.setLatLngs([latlngs])
            else layer.setLatLngs(latlngs)
            
            // Note: We don't rebuild intermediates during drag to save perf, 
            // or we could calls rebuild() throttled. 
            // For now, let's just update the layer.
         }
         
         const onUp = async () => {
           map.off('mousemove', onMove)
           map.off('mouseup', onUp)
           map.dragging.enable()
           
           // Save changes API
           rebuild() // Rebuild to sync intermediates to new positions
           await saveLayerChanges(layer, markerId, isPolygon)
         }
         
         map.on('mousemove', onMove)
         map.on('mouseup', onUp)
       })
    })

    // 2. Intermediate Markers
    for (let i = 0; i < latlngs.length; i++) {
        if (!isPolygon && i === latlngs.length - 1) break
        
        const p1 = latlngs[i]
        const p2 = latlngs[(i + 1) % latlngs.length]
        
        const mid = L.latLng((p1.lat + p2.lat)/2, (p1.lng + p2.lng)/2)
        
        const iMarker = L.circleMarker(mid, {
          radius: 6, color: '#2563EB', weight: 2, fillColor: '#fff', fillOpacity: 0.5, opacity: 0.5
        }).addTo(markerLayer.value)
        
        context.handles.push(iMarker)
        
        const onInsert = async (e) => {
            L.DomEvent.stopPropagation(e)
            // Insert at i+1
            latlngs.splice(i + 1, 0, mid)
            
            if (isPolygon) layer.setLatLngs([latlngs])
            else layer.setLatLngs(latlngs)
            
            rebuild()
            await saveLayerChanges(layer, markerId, isPolygon)
        }

        iMarker.on('click', onInsert)
        // Drag logic for intermediate: equivalent to insert + drag
        iMarker.on('mousedown', (e) => {
             L.DomEvent.stopPropagation(e)
             // Insert immediately
             latlngs.splice(i + 1, 0, mid)
             if (isPolygon) layer.setLatLngs([latlngs])
             else layer.setLatLngs(latlngs)
             
             // Now find the new vertex marker we just logically created, 
             // but 'rebuild()' hasn't run yet so we don't have the handle.
             // We must rebuild to get the handle to drag it.
             rebuild()
             
             // The new vertex is at index i+1.
             // context.handles has standard vertices first.
             const vertexHandleIndex = i + 1
             const vertexHandle = context.handles[vertexHandleIndex]
             
             if (vertexHandle) {
                 // Trigger drag start on it
                 // We need to manufacture a mousedown event or manually call the logic
                 // Since we wrapped logic in closure, let's just wait for user to click again?
                 // No, native feel needs immediate drag.
                 // We can extract startDrag logic.
                 // For now, simpler: Click = Insert. Drag = Insert + Wait.
                 // Since we rebuilt, the user has to click again to drag. 
                 // We can leave this as "Click to insert" for robust MVP.
             }
             saveLayerChanges(layer, markerId, isPolygon)
        })
    }
  }

  rebuild()
}

async function saveLayerChanges(layer, id, isPolygon) {
   let latlngs = layer.getLatLngs()
   if (isPolygon && Array.isArray(latlngs) && Array.isArray(latlngs[0])) latlngs = latlngs[0]
   
   // need scaling
   const savedPts = latlngs.map(p => ({
       lat: Number((p.lat * (scaleY || 1)).toFixed(6)), 
       lng: Number((p.lng * (scaleX || 1)).toFixed(6)) 
   }))
   
   // Recover color/type from layer options or marker metadata
   // We stored 'imgName' in the layer options!
   const oldImgName = layer.options.imgName || ''
   let newImgName = oldImgName
   
   // В line:… и polygon:… точки всегда последний сегмент — меняем только его,
   // стиль (цвет, тип, толщина, заливка) сохраняется как был.
   if (oldImgName.startsWith('line:') || oldImgName.startsWith('polygon:')) {
       const parts = oldImgName.split(':')
       if (parts.length >= 4) {
           const pointsStr = savedPts.map(p => `${p.lat},${p.lng}`).join(';')
           newImgName = [...parts.slice(0, -1), pointsStr].join(':')
       }
   }
   
   layer.options.imgName = newImgName
   const first = savedPts[0]
   
   try {
      await updateMapMarker(id, { lat: first.lat, lng: first.lng, img_name: newImgName })
   } catch(e) {
      console.error('Update marker geometry failed', e)
   }
}

</script>

<style scoped>
.map-shell {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.map {
  flex: 1;
  height: 100%;
  min-height: 500px;
  width: 100%;
  border-radius: 8px;
  position: relative;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

/* ====== Панель выделенного знака (внизу по центру карты) ====== */
.el-panel {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  max-width: calc(100% - 32px);
  padding: 8px 40px 8px 12px;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  color: #111827;
}
.el-panel__title {
  font-weight: 700;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.el-panel__group { display: inline-flex; align-items: center; gap: 4px; }
.el-panel__label { color: #6B7280; margin-right: 2px; }
.el-panel__range { width: 110px; accent-color: #2563EB; }
.el-panel__value { min-width: 38px; text-align: right; font-variant-numeric: tabular-nums; color: #374151; }
.el-panel__btn {
  min-width: 26px;
  height: 26px;
  padding: 0 4px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  font-family: inherit;
}
.el-panel__btn--wide { font-size: 11px; font-weight: 600; }
.el-panel__btn:hover { background: #EFF6FF; border-color: #2563EB; }
.el-panel__swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #D1D5DB;
  cursor: pointer;
}
.el-panel__swatch.is-active { box-shadow: 0 0 0 2px #2563EB; }
.el-panel__delete {
  height: 26px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: #DC2626;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.el-panel__delete:hover { background: #B91C1C; }
.el-panel__close {
  position: absolute;
  top: 6px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  font-size: 18px;
  color: #6B7280;
  cursor: pointer;
}

.map-init-error {
  position: absolute;
  left: 12px;
  bottom: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #111827;
}
.grid-label { pointer-events: none; }
.grid-label span {
  cursor: default;
  font-size: 12px;
  font-weight: bold;
  color:#222;
  background: rgba(255,255,255,0.7);
  padding:2px 4px;
}

:deep(.leaflet-control-attribution) {
  display: none !important;
}

/* ====== Улитка: координата B12-3 при движении мыши ====== */
.ulitka-panel {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  font-size: 13px;
  color: #111827;
  user-select: none;
}
.ulitka-panel__toggle { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-weight: 500; }
.ulitka-panel__toggle input { accent-color: #DC2626; cursor: pointer; }
.ulitka-panel__coord {
  padding: 4px 10px;
  background: #FEE2E2;
  color: #991B1B;
  border-radius: 6px;
  font-family: 'Menlo', 'Courier New', monospace;
  font-weight: 700;
  min-width: 68px;
  text-align: center;
  letter-spacing: 0.5px;
}
.ulitka-panel__gear {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6B7280;
  font-size: 16px;
  padding: 2px 6px;
  border-radius: 6px;
  transition: background .15s;
}
.ulitka-panel__gear:hover { background: #F3F4F6; color: #111827; }

/* Панель калибровки сетки */
.grid-calibration-panel {
  position: absolute;
  top: 58px;
  left: 12px;
  z-index: 550;
  width: 280px;
  padding: 12px 14px;
  background: rgba(255,255,255,0.98);
  border: 1px solid #D1D5DB;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  font-size: 12px;
  color: #111827;
  user-select: none;
}
.grid-calibration-panel__title {
  font-weight: 700;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.grid-calibration-panel__close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6B7280;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
}
.grid-calibration-panel__hint {
  color: #6B7280;
  font-size: 11px;
  margin-bottom: 10px;
  line-height: 1.3;
}
.grid-calibration-panel__row {
  display: grid;
  grid-template-columns: 60px 1fr 46px;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}
.grid-calibration-panel__row label { font-weight: 500; color: #374151; }
.grid-calibration-panel__value {
  font-family: 'Menlo','Courier New',monospace;
  font-size: 11px;
  color: #6B7280;
  text-align: right;
}
.grid-calibration-panel__row input[type="range"] { width: 100%; accent-color: #DC2626; }
.grid-calibration-panel__reset {
  margin-top: 6px;
  width: 100%;
  padding: 6px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  color: #374151;
}
.grid-calibration-panel__reset:hover { background: #E5E7EB; }

/* Undo / Redo */
.map-history {
  position: absolute;
  top: 12px;
  right: 60px;
  z-index: 500;
  display: flex;
  gap: 4px;
  background: rgba(255,255,255,0.96);
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  padding: 2px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}
.map-history__btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  font-size: 18px;
  color: #374151;
  transition: background .15s, color .15s;
}
.map-history__btn:hover { background: #F3F4F6; color: #111827; }
.map-history__btn:active { background: #E5E7EB; }

/* Popup улитки по клику */
:deep(.ulitka-leaflet-popup .leaflet-popup-content-wrapper) {
  border-radius: 12px;
  padding: 4px 6px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}
:deep(.ulitka-popup) {
  font-family: system-ui, -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
}
:deep(.ulitka-popup__label) {
  font-size: 11px;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
:deep(.ulitka-popup__value) {
  font-size: 22px;
  font-weight: 800;
  color: #991B1B;
  font-family: 'Menlo', 'Courier New', monospace;
  letter-spacing: 1px;
}
:deep(.ulitka-popup__meta) {
  font-size: 11px;
  color: #4B5563;
}
:deep(.ulitka-popup__copy) {
  margin-top: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: #DC2626;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-size: 12px;
  transition: background .15s;
}
:deep(.ulitka-popup__copy:hover) { background: #B91C1C; }

/* Всплывающая метка на карте, следующая за курсором */
:deep(.ulitka-badge) {
  background: rgba(220, 38, 38, 0.95);
  color: #fff;
  border-radius: 6px;
  padding: 3px 8px;
  font-family: 'Menlo', 'Courier New', monospace;
  font-weight: 700;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

/* ====== Вертикальный zoom-слайдер справа ====== */
.map-zoom-slider {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  z-index: 500;
  width: 44px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #D1D5DB;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  user-select: none;
}

.map-zoom-slider__btn {
  width: 32px;
  height: 32px;
  border: 1px solid #D1D5DB;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  padding: 0;
  transition: background 0.12s, border-color 0.12s;
  font-family: inherit;
}
.map-zoom-slider__btn:hover { background: #F3F4F6; border-color: #9CA3AF; }
.map-zoom-slider__btn:active { background: #E5E7EB; }

.map-zoom-slider__track {
  position: relative;
  height: 220px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Vertical range (rotated) */
.map-zoom-slider__range {
  width: 220px;
  height: 6px;
  transform: rotate(-90deg);
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  margin: 0;
}
.map-zoom-slider__range::-webkit-slider-runnable-track {
  height: 6px;
  background: linear-gradient(90deg, #E5E7EB 0%, #93C5FD 50%, #2F78EE 100%);
  border-radius: 3px;
}
.map-zoom-slider__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  background: #2F78EE;
  border: 3px solid #fff;
  border-radius: 50%;
  cursor: grab;
  margin-top: -8px;
  box-shadow: 0 2px 6px rgba(47, 120, 238, 0.4);
}
.map-zoom-slider__range::-webkit-slider-thumb:active { cursor: grabbing; }
.map-zoom-slider__range::-moz-range-track {
  height: 6px;
  background: linear-gradient(90deg, #E5E7EB 0%, #93C5FD 50%, #2F78EE 100%);
  border-radius: 3px;
}
.map-zoom-slider__range::-moz-range-thumb {
  width: 22px;
  height: 22px;
  background: #2F78EE;
  border: 3px solid #fff;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 2px 6px rgba(47, 120, 238, 0.4);
}

.map-zoom-slider__badge {
  position: absolute;
  left: -46px;
  top: 50%;
  transform: translateY(-50%);
  padding: 3px 8px;
  background: #111827;
  color: #fff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  min-width: 34px;
  text-align: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  font-family: 'Menlo', monospace;
}
.map-zoom-slider:hover .map-zoom-slider__badge,
.map-zoom-slider__range:focus + .map-zoom-slider__badge { opacity: 1; }
.map-zoom-slider__badge::after {
  content: '';
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-left-color: #111827;
}
</style>

<!-- Глобальные стили Leaflet — без scoped, чтобы попасть на элементы карты -->
<style>
/* Разметка знаков создаётся Leaflet (divIcon) вне шаблона Vue — scoped-стили
   на неё не действуют, поэтому стили знака здесь, под префиксом .map-shell. */
.map-shell .map-marker__wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Масштаб через CSS-переменную --marker-scale на контейнере карты.
     По умолчанию duration = 0s — новые маркеры появляются мгновенно, не «прыгают».
     При zoomanim (см. onZoomAnim) duration ставится в 0.25s — синхронно с Leaflet. */
  transform: scale(var(--marker-scale, 1));
  transform-origin: center center;
  transition: transform var(--marker-scale-duration, 0s) ease-out;
  will-change: transform;
}

/* Элемент знака: поворачивается вокруг точки привязки (transform-origin inline) */
.map-shell .map-el {
  position: absolute;
  left: 0;
  top: 0;
}
/* Рамка и ручки растяжения выделенного знака */
.map-shell .map-el__frame {
  position: absolute;
  inset: -3px;
  border: 1px dashed #2563EB;
  pointer-events: none;
}
.map-shell .map-el__handle {
  position: absolute;
  width: 10px;
  height: 10px;
  margin: -5px 0 0 -5px;
  background: #fff;
  border: 2px solid #2563EB;
  border-radius: 2px;
  box-sizing: border-box;
  z-index: 11;
}
.map-shell .map-el__handle--n { left: 50%; top: -3px; cursor: ns-resize; }
.map-shell .map-el__handle--s { left: 50%; top: calc(100% + 3px); cursor: ns-resize; }
.map-shell .map-el__handle--e { left: calc(100% + 3px); top: 50%; cursor: ew-resize; }
.map-shell .map-el__handle--w { left: -3px; top: 50%; cursor: ew-resize; }

.map-shell .map-marker--ghost,
.map-shell .map-marker--ghost * { pointer-events: none !important; }
.map-shell .map-el svg { display: block; }

.map-shell .map-marker__wrap img {
  display:block;
}

/* Подпись под значком (номер / состав / время) — по ТЗ встречи 27.08.2026 п.9 */
.map-shell .map-marker__caption {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 2px;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(255,255,255,0.92);
  color: #111827;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Menlo','Courier New',monospace;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  border: 1px solid rgba(0,0,0,0.08);
  pointer-events: none;
  line-height: 1.2;
}

.map-shell .map-marker__delete {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: #DC2626;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/*
  Контрольно-следовая полоса (КСП) — топографический знак:
  — .ksp-line--band: жирная сплошная чёрная полоса (пахоба/борозда);
  — .ksp-line--dots: чёрные точки поверх (штрих борозды).
  Раньше был один слой с stroke-dasharray:6 4, что читалось как пунктир —
  не соответствовало ТЗ. Теперь два слоя, стили заданы в inline-options
  L.polyline; здесь только hover/focus.
*/
.leaflet-interactive.ksp-line { cursor: pointer; }
.leaflet-interactive.ksp-line--dots { pointer-events: none; }
/* Улитка-бейдж */
.ulitka-badge {
  background: rgba(17, 24, 39, 0.85);
  color: #fff;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
}
</style>
